import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  BsGear, BsThreeDotsVertical, BsPaperclip, BsReply, BsReplyAll, BsForward, BsArchive, BsTrash, BsInbox,
  BsSend, BsExclamationTriangle, BsEnvelope, BsEnvelopeOpen, BsArrowCounterclockwise, BsShieldX, BsClock,
  BsFileEarmarkRichtext, BsRobot, BsCheck2All, BsBriefcase, BsPencilSquare
} from 'react-icons/bs';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { apiFetch, parseJsonSafe } from '../utils/api';
import EmailBody from '../components/EmailBody';
import EmailRecipientInput from '../components/EmailRecipientInput';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const statusOptions = ['nuevo','candidato_ia','pendiente_revision','auto_resuelto','archivado'];

// Carpetas del buzón. Las de mensajes consultan /api/communications?folder=…; las tres
// últimas muestran las vistas de programados, borradores y plantillas (scheduled_emails).
const FOLDERS = [
  { key: 'inbox', label: 'Recibidos', Icon: BsInbox },
  { key: 'pending', label: 'Pendientes IA', Icon: BsRobot },
  { key: 'sent', label: 'Enviados', Icon: BsSend },
  { key: 'archived', label: 'Archivados', Icon: BsArchive },
  { key: 'spam', label: 'Descartados', Icon: BsShieldX },
  { key: 'trash', label: 'Papelera', Icon: BsTrash },
  { key: 'scheduled', label: 'Programados', Icon: BsClock },
  { key: 'drafts', label: 'Borradores', Icon: BsPencilSquare },
  { key: 'templates', label: 'Plantillas', Icon: BsFileEarmarkRichtext }
];
const MESSAGE_FOLDERS = ['inbox', 'pending', 'sent', 'archived', 'spam', 'trash'];
const folderLabel = (key) => (FOLDERS.find((f) => f.key === key) || {}).label || key;
const SCHEDULED_STATUS_LABELS = { pending: 'Pendiente de envío', sending: 'Enviando…', sent: 'Enviado', error: 'Error', cancelled: 'Cancelado', draft: 'Borrador' };
const scheduledStatusLabel = (status) => SCHEDULED_STATUS_LABELS[status] || status;

const DISCARD_REASON_LABELS = {
  'spam-server': 'Marcado como spam por el servidor',
  'bounce-sender': 'Rebote / aviso de entrega',
  'bounce-subject': 'Rebote / aviso de entrega',
  'system-domain': 'Notificación de sistema o red social',
  'bulk-mail': 'Envío masivo (plataforma de email marketing)',
  'system-subject': 'Aviso automático de sistema',
  'otp-subject': 'Código de verificación',
  'own-domain': 'Correo interno del propio dominio',
  'foreign-script': 'Alfabeto no latino',
  newsletter: 'Newsletter / suscripción',
  promo: 'Promoción / oferta',
  outreach: 'Captación en frío',
  manual: 'Descartado manualmente'
};
const discardReasonLabel = (r) => DISCARD_REASON_LABELS[r] || r || 'Regla del filtro';

const ACTION_TYPE_LABELS = {
  create_task: 'Crear tarea', create_event: 'Crear evento en la agenda', update_event: 'Actualizar evento', delete_event: 'Eliminar evento',
  send_invoices: 'Adjuntar facturas', send_budget: 'Adjuntar presupuesto', create_support_ticket: 'Abrir ticket de soporte',
  create_dev_request: 'Crear cambio solicitado por el cliente'
};

const escapeHtml = (str) => String(str || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const splitAddresses = (value) => String(value || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean);
const bareEmail = (value) => {
  const m = String(value || '').match(/<([^>]+)>/);
  return (m ? m[1] : String(value || '')).trim().toLowerCase();
};

const badgeClass = (status) => {
  switch (status) {
    case 'candidato_ia': return 'badge bg-warning text-dark';
    case 'pendiente_revision': return 'badge bg-info text-dark';
    case 'auto_resuelto': return 'badge bg-success';
    case 'spam': return 'badge bg-danger';
    default: return 'badge bg-secondary';
  }
};

const STATUS_LABELS = {
  nuevo: 'Nuevo',
  candidato_ia: 'Candidato IA',
  pendiente_revision: 'Pendiente revisión',
  auto_resuelto: 'Auto-resuelto',
  spam: 'Descartado',
  sistema: 'Enviado',
  archivado: 'Archivado'
};
const statusLabel = (status) => STATUS_LABELS[status] || status || '';
// Etiqueta de estado de una fila: los salientes se muestran como Enviado / Rebotado
const rowStatusLabel = (m) => {
  if (!m) return '';
  if (m.direccion === 'out') return m.bounced ? 'Rebotado' : (m.sent_via === 'imap' ? 'Enviado desde Outlook' : 'Enviado');
  return statusLabel(m.estado);
};
const rowBadgeClass = (m) => {
  if (!m) return 'badge bg-secondary';
  if (m.direccion === 'out') return m.bounced ? 'badge bg-danger' : (m.sent_via === 'imap' ? 'badge bg-info text-dark' : 'badge bg-success bg-opacity-75');
  return badgeClass(m.estado);
};

const INTENT_LABELS = {
  facturas: 'Facturas',
  facturas_respuesta: 'Facturas',
  agenda: 'Agenda',
  agenda_respuesta: 'Agenda',
  reunion: 'Reunión',
  reunión: 'Reunión',
  tarea: 'Tarea',
  tarea_responder: 'Tarea',
  consulta: 'Consulta',
  presupuesto: 'Presupuesto',
  presupuesto_respuesta: 'Presupuesto',
  duda_tecnica: 'Soporte',
  soporte: 'Soporte',
  respuesta: 'Respuesta',
  derivar_comercial: 'Agente comercial',
  seguimiento_comercial: 'Seguimiento comercial',
  spam: 'Spam',
  otro: 'Otro'
};
const intentLabel = (intent) => {
  if (!intent) return '';
  return INTENT_LABELS[String(intent).toLowerCase()] || intent;
};

const CommunicationsPage = () => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const initialParams = new URLSearchParams(location.search || '');
  const [folder, setFolder] = useState(() => {
    const f = initialParams.get('folder');
    return FOLDERS.some((x) => x.key === f) ? f : 'inbox';
  });
  const [folderCounts, setFolderCounts] = useState({});
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [attachmentsOnly, setAttachmentsOnly] = useState(false);
  const [accountFilter, setAccountFilter] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Editor de respuesta: modo (reply | replyAll | forward | null), destinatarios y opciones
  const [replyMode, setReplyMode] = useState(null);
  const [replyTo, setReplyTo] = useState('');
  const [replyCc, setReplyCc] = useState('');
  const [replyBcc, setReplyBcc] = useState('');
  const [showReplyCc, setShowReplyCc] = useState(false);
  const [includeQuote, setIncludeQuote] = useState(true);
  const [forwardAttachmentIdx, setForwardAttachmentIdx] = useState([]);
  const [replyTemplateId, setReplyTemplateId] = useState('');
  const [downloadingAtt, setDownloadingAtt] = useState(null);
  const openRequested = useRef(initialParams.get('open') || null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingActions, setLoadingActions] = useState({ poll: false, process: false, processSelected: false });
  const [showConfig, setShowConfig] = useState(false);
  const [pollIntervalValue, setPollIntervalValue] = useState(0);
  const [pollIntervalUnit, setPollIntervalUnit] = useState('minutes');
  const [pollAutoProcess, setPollAutoProcess] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySubject, setReplySubject] = useState('');
  // Valores iniciales del editor "Responder": si el usuario los modifica, "Aprobar y
  // ejecutar" debe enviar SU versión, nunca la propuesta original de la IA
  const [replyDraftInitial, setReplyDraftInitial] = useState('');
  const [replySubjectInitial, setReplySubjectInitial] = useState('');
  // Edición del borrador de la IA ANTES de aprobar: null = se envía tal cual lo sugirió
  const [approveEdit, setApproveEdit] = useState(null);
  const [replyAccountId, setReplyAccountId] = useState('');
  const [replyLastSignature, setReplyLastSignature] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyUploading, setReplyUploading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [nanoLogs, setNanoLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeLoading, setComposeLoading] = useState(false);
  const [composeUploading, setComposeUploading] = useState(false);
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [compose, setCompose] = useState({ to: '', cc: '', bcc: '', subject: '', body: '', accountId: '', sendAt: '', lastSignature: '' });
  const [composeEditingId, setComposeEditingId] = useState(null);
  const [composeEditingStatus, setComposeEditingStatus] = useState('');
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduledStatus, setScheduledStatus] = useState('pending');
  const [sendingNowId, setSendingNowId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ id: null, name: '', body: '' });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const composeFileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const prefillApplied = useRef(false);
  const [executingActions, setExecutingActions] = useState(false);
  const [execMessage, setExecMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [defaultTaskUserId, setDefaultTaskUserId] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showExecutedActions, setShowExecutedActions] = useState(false);
  const [groupByThread, setGroupByThread] = useState(false);
  const [threadMessages, setThreadMessages] = useState([]);
  const [openThreadId, setOpenThreadId] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [modalMessage, setModalMessage] = useState(null); // { type: 'success'|'danger', text }
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  // Entrenamiento IA
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState({});
  const [countResult, setCountResult] = useState(null);
  const syncPollRef = useRef(null);
  const detailRequestIdRef = useRef(null);
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const authFetch = async (path, options = {}) => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      handleUnauthorized();
      throw new Error('Token inválido o expirado');
    }
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${currentToken}`
    };
    const res = await apiFetch(path, { ...options, headers });
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error('Token inválido o expirado');
    }
    return res;
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Debounce para campos de texto
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedFrom, setDebouncedFrom] = useState('');
  const [debouncedTo, setDebouncedTo] = useState('');
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setDebouncedFrom(fromFilter);
      setDebouncedTo(toFilter);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search, fromFilter, toFilter]);

  const hasActiveFilters = fromFilter || toFilter || dateFromFilter || dateToFilter || directionFilter || unreadOnly || attachmentsOnly || accountFilter;
  const clearAllFilters = () => {
    setSearch(''); setFromFilter(''); setToFilter('');
    setDateFromFilter(''); setDateToFilter('');
    setDirectionFilter(''); setStatusFilter(''); setPage(1);
    setUnreadOnly(false); setAttachmentsOnly(false); setAccountFilter('');
  };

  const isMessageFolder = MESSAGE_FOLDERS.includes(folder);
  // Programados nunca muestra borradores (ni con «Todos»); Borradores solo borradores
  const visibleScheduled = scheduledEmails.filter((m) => (folder === 'drafts' ? m.status === 'draft' : m.status !== 'draft'));

  const fetchFolderCounts = async () => {
    try {
      const res = await apiFetch('/api/communications/folders', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok && data) setFolderCounts(data);
    } catch (_e) { /* silencioso */ }
    return null;
  };

  const changeFolder = (next) => {
    if (next === folder) return;
    setFolder(next);
    setPage(1);
    setSelectedIds([]);
    setSelectAll(false);
    setStatusFilter('');
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('folder', next);
      url.searchParams.delete('open');
      window.history.replaceState({}, '', url.toString());
    } catch (_e) { /* sin historial */ }
  };

  const fetchData = async () => {
    if (!isMessageFolder) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize, folder });
      if (statusFilter) params.append('status', statusFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (debouncedFrom) params.append('from', debouncedFrom);
      if (debouncedTo) params.append('to', debouncedTo);
      if (dateFromFilter) params.append('dateFrom', dateFromFilter);
      if (dateToFilter) params.append('dateTo', dateToFilter);
      if (directionFilter) params.append('direction', directionFilter);
      if (unreadOnly) params.append('unread', '1');
      if (attachmentsOnly) params.append('attachments', '1');
      if (accountFilter) params.append('account', accountFilter);
      if (groupByThread) params.append('groupByThread', 'true');
      const res = await apiFetch(`/api/communications?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando comunicaciones');
      setItems(data.items || []);
      setTotal(data.total || 0);
      fetchFolderCounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Leído / no leído y acciones en bloque (archivar, papelera, restaurar, descartar…)
  const markRead = async (ids, leido) => {
    if (!ids.length) return;
    try {
      const res = await apiFetch('/api/communications/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ ids, leido })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setItems((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, leido } : m)));
      setDetail((prev) => (prev && ids.includes(prev.id) ? { ...prev, leido } : prev));
      fetchFolderCounts();
    } catch (err) {
      setError(err.message);
    }
    return null;
  };

  const BULK_LABELS = {
    archive: 'archivadas', unarchive: 'devueltas a Recibidos', trash: 'movidas a la papelera', restore: 'restauradas',
    spam: 'descartadas', not_spam: 'recuperadas a Recibidos', delete: 'eliminadas definitivamente'
  };
  const bulkAction = async (action, ids, { confirmText } = {}) => {
    if (!ids.length) return false;
    if (confirmText && !window.confirm(confirmText)) return false;
    setBulkBusy(true);
    try {
      const res = await apiFetch('/api/communications/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ ids, action })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo aplicar la acción');
      setSelectedIds([]);
      setSelectAll(false);
      setError('');
      if (detail && ids.includes(detail.id)) {
        if (['trash', 'delete', 'spam', 'archive', 'not_spam'].includes(action)) closeDetail();
        else if (action === 'restore' || action === 'unarchive') setDetail((prev) => (prev ? { ...prev, deleted_at: action === 'restore' ? null : prev.deleted_at } : prev));
      }
      fetchData();
      if (action === 'not_spam') setExecMessage(`${data.updated || 0} comunicación(es) ${BULK_LABELS[action]}; la IA las está procesando.`);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBulkBusy(false);
    }
  };

  const emptyTrash = async () => {
    if (!window.confirm('¿Vaciar la papelera? Se eliminarán definitivamente todos los mensajes que contiene.')) return;
    setBulkBusy(true);
    try {
      const res = await apiFetch('/api/communications/trash/empty', { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo vaciar la papelera');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkBusy(false);
    }
    return null;
  };

  // Descarga autenticada de un adjunto (entrante o saliente)
  const downloadAttachment = async (msgId, att) => {
    const key = `${msgId}-${att.index}`;
    setDownloadingAtt(key);
    try {
      const res = await apiFetch(`/api/communications/${msgId}/attachments/${att.index}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error(data.message || 'No se pudo descargar');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'adjunto';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      setModalMessage({ type: 'danger', text: err.message });
    } finally {
      setDownloadingAtt(null);
    }
    return null;
  };

  const fetchPollConfig = async () => {
    try {
      const res = await apiFetch('/api/integrations/email-poll-config', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && data) {
        setPollIntervalValue(data.value ?? 0);
        setPollIntervalUnit(data.unit || 'minutes');
        setPollAutoProcess(Boolean(data.autoProcessIA));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEmailAccounts = async () => {
    try {
      const res = await apiFetch('/api/email-accounts', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) {
        setEmailAccounts(data);
        if (!compose.accountId && data[0]) {
          const sig = data[0].signature ? `\n\n${data[0].signature}` : '';
          setCompose((p) => ({ ...p, accountId: data[0].id, lastSignature: sig, body: replaceSignature(p.body, p.lastSignature, sig) }));
        }
      } else if (!res.ok) {
        setError((data && data.message) || 'Error obteniendo cuentas de email');
      }
    } catch (err) {
      console.error(err);
      setError('Error de red obteniendo cuentas de email');
    }
  };

  const fetchNanoLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await apiFetch('/api/email/nano-logs?limit=100', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) setNanoLogs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDefaultTaskUser = async () => {
    try {
      const res = await apiFetch('/api/tasks/config/default-user', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) setDefaultTaskUserId(data?.default_user_id ? String(data.default_user_id) : '');
    } catch (err) {
      console.error(err);
    }
  };

  const savePollConfig = async () => {
    setSavingConfig(true);
    try {
      const pollRes = await apiFetch('/api/integrations/email-poll-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value: pollIntervalValue, unit: pollIntervalUnit, autoProcessIA: pollAutoProcess })
      });
      const pollData = await parseJsonSafe(pollRes);
      if (pollRes.status === 401) return handleUnauthorized();
      if (!pollRes.ok) throw new Error(pollData.message || 'No se pudo guardar la programación');

      const userRes = await apiFetch('/api/tasks/config/default-user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ default_user_id: defaultTaskUserId || null })
      });
      const userData = await parseJsonSafe(userRes);
      if (userRes.status === 401) return handleUnauthorized();
      if (!userRes.ok) throw new Error(userData.message || 'No se pudo guardar el usuario por defecto');

      alert('Configuración guardada');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Funciones de Entrenamiento IA ──
  const startSyncStatusPoll = () => {
    if (syncPollRef.current) return;
    syncPollRef.current = setInterval(async () => {
      try {
        const freshToken = localStorage.getItem('token');
        const res = await apiFetch('/api/email/sync/status', { headers: { Authorization: `Bearer ${freshToken}` } });
        const data = await parseJsonSafe(res);
        setSyncStatus(data);
        if (!data.active && data.status !== 'running') {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncLoading({});
        }
      } catch (_e) {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
      }
    }, 3000);
  };

  const handleSyncAction = async (action) => {
    setSyncLoading((prev) => ({ ...prev, [action]: true }));
    setSyncStatus(null);
    setCountResult(null);
    try {
      const res = await apiFetch(`/api/email/sync/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (action === 'count-imap') {
        setCountResult(data);
        setSyncLoading((prev) => ({ ...prev, [action]: false }));
      } else {
        setSyncStatus({ active: true, type: action, status: 'running', startedAt: new Date().toISOString() });
        startSyncStatusPoll();
      }
    } catch (err) {
      setError(err.message);
      setSyncLoading((prev) => ({ ...prev, [action]: false }));
    }
  };

  useEffect(() => {
    return () => { if (syncPollRef.current) clearInterval(syncPollRef.current); };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, folder, statusFilter, debouncedSearch, debouncedFrom, debouncedTo, dateFromFilter, dateToFilter, directionFilter, unreadOnly, attachmentsOnly, accountFilter, groupByThread]);

  // Programados y Borradores se leen de scheduled_emails, no del buzón: recargar al entrar
  useEffect(() => {
    if (folder === 'scheduled' || folder === 'drafts') fetchScheduled(undefined, folder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  // Abrir un mensaje concreto desde una notificación (/comunicaciones?open=ID)
  useEffect(() => {
    if (!openRequested.current) return;
    const id = parseInt(openRequested.current, 10);
    openRequested.current = null;
    if (Number.isInteger(id)) handleRowClick(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPollConfig();
    fetchDefaultTaskUser();
    fetchUsers();
    fetchEmailAccounts();
    fetchNanoLogs();
    fetchScheduled();
    fetchTemplates();
    fetchFolderCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, [items]);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const handleRowClick = async (id) => {
    // "última petición gana": si se abre otra fila antes de que termine, descartamos esta respuesta
    detailRequestIdRef.current = id;
    // Limpieza de adjuntos de una respuesta previa no enviada (evita huérfanos en el servidor)
    if (replyAttachments.length) {
      cleanupUploadedAttachments(replyAttachments.filter((att) => !att.persisted));
    }
    setLoadingDetail(true);
    setThreadMessages([]);
    setOpenThreadId(null);
    setModalMessage(null);
    try {
      const res = await apiFetch(`/api/communications/${id}?markRead=1`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando detalle');
      if (detailRequestIdRef.current !== id) return; // llegó tarde: el usuario abrió otra comunicación
      setDetail(data);
      // Al abrirlo queda leído (la lista y los contadores se actualizan sin recargar)
      setItems((prev) => prev.map((m) => (m.id === id && !m.leido ? { ...m, leido: true } : m)));
      if (data.leido) fetchFolderCounts();
      const accountMatch = emailAccounts.find((a) => a.from_email === data.email_account || a.imap_user === data.email_account);
      const selectedAccountId = accountMatch ? accountMatch.id : (emailAccounts[0]?.id || '');
      const signature = getSignature(selectedAccountId);
      const baseBody = data.ai_suggested_body ? textToHtml(data.ai_suggested_body) : '';
      setReplyDraft(`${baseBody}${signature || ''}`);
      setReplyDraftInitial(`${baseBody}${signature || ''}`);
      setReplyLastSignature(signature);
      const baseSubject = data.ai_suggested_subject || data.asunto || '';
      const initialSubject = baseSubject && !baseSubject.toLowerCase().startsWith('re:') ? `Re: ${baseSubject}` : baseSubject;
      setReplySubject(initialSubject);
      setReplySubjectInitial(initialSubject);
      setReplyAccountId(selectedAccountId);
      setReplyAttachments([]);
      setApproveEdit(null);
      setExecMessage('');
      // Editor: los entrantes abren directamente en "Responder" al remitente; los salientes
      // solo ofrecen reenviar (o escribir de nuevo al destinatario)
      // Editor de respuesta: abierto por defecto solo cuando hace falta contestar a mano
      // (correo entrante sin propuesta de la IA y sin resolver). Si hay propuesta pendiente, la
      // tarjeta de la IA es el camino principal y el editor se abre con "Responder".
      const dataActions = parseAiActions(data.ai_actions);
      const dataHasProposal = Boolean(data.ai_suggested_body || dataActions.length);
      const dataResolved = Boolean(data.ai_executed_at || data.estado === 'auto_resuelto' || data.replied_at);
      setReplyMode(data.direccion === 'in' && data.estado !== 'spam' && !data.deleted_at && !dataHasProposal && !dataResolved ? 'reply' : null);
      setReplyTo(data.direccion === 'in' ? (data.from_address || bareEmail(data.remitente) || '') : (data.to_address || ''));
      setReplyCc('');
      setReplyBcc('');
      setShowReplyCc(false);
      setIncludeQuote(true);
      setForwardAttachmentIdx([]);
      setReplyTemplateId('');
      // cargar hilo completo
      const threadRes = await apiFetch(`/api/communications/${id}/thread`, { headers: { Authorization: `Bearer ${token}` } });
      const threadData = await parseJsonSafe(threadRes);
      if (detailRequestIdRef.current !== id) return; // el hilo también puede llegar tarde
      if (threadRes.ok && Array.isArray(threadData)) {
        setThreadMessages(threadData);
      } else if (!threadRes.ok && threadRes.status === 401) {
        return handleUnauthorized();
      }
    } catch (err) {
      if (detailRequestIdRef.current !== id) return;
      setError(err.message);
      setDetail(null);
    } finally {
      if (detailRequestIdRef.current === id) setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    // Limpia del servidor los adjuntos de respuesta subidos pero no enviados
    if (replyAttachments.length) {
      cleanupUploadedAttachments(replyAttachments.filter((att) => !att.persisted));
    }
    detailRequestIdRef.current = null;
    setReplyAttachments([]);
    setModalMessage(null);
    setReplyMode(null);
    setDetail(null);
  };

  // Recarga el detalle y el hilo tras enviar (para ver la respuesta en la conversación
  // y las marcas de respondido/reenviado sin cerrar y volver a abrir)
  const refreshDetail = async (id) => {
    try {
      const [res, threadRes] = await Promise.all([
        apiFetch(`/api/communications/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        apiFetch(`/api/communications/${id}/thread`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      ]);
      if (res.status === 401 || threadRes.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      const threadData = await parseJsonSafe(threadRes);
      if (res.ok && detailRequestIdRef.current === id) setDetail(data);
      if (threadRes.ok && Array.isArray(threadData) && detailRequestIdRef.current === id) setThreadMessages(threadData);
    } catch (_e) { /* silencioso */ }
    return null;
  };

  // Direcciones propias (cuentas configuradas): se excluyen de "Responder a todos"
  const ownAddresses = useMemo(() => new Set(
    emailAccounts.flatMap((a) => [a.from_email, a.imap_user, a.smtp_user]).filter(Boolean).map((x) => String(x).toLowerCase())
  ), [emailAccounts]);

  const originalBodyHtml = (msg) => {
    if (!msg) return '';
    if (msg.cuerpo_html) return DOMPurify.sanitize(msg.cuerpo_html, { USE_PROFILES: { html: true } });
    return `<div style="white-space:pre-wrap">${escapeHtml(msg.cuerpo_texto || '')}</div>`;
  };
  const buildQuoteHtml = (msg) => {
    if (!msg) return '';
    const when = msg.fecha ? new Date(msg.fecha).toLocaleString('es-ES') : '';
    return `<br><br><div class="gmail_quote"><div style="color:#666;font-size:0.9em">El ${escapeHtml(when)}, ${escapeHtml(msg.remitente || msg.from_address || '')} escribió:</div>`
      + `<blockquote style="margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex">${originalBodyHtml(msg)}</blockquote></div>`;
  };
  const buildForwardHtml = (msg) => {
    if (!msg) return '';
    const when = msg.fecha ? new Date(msg.fecha).toLocaleString('es-ES') : '';
    return `<br><br><div class="gmail_quote"><div style="color:#666">---------- Mensaje reenviado ----------<br>`
      + `<b>De:</b> ${escapeHtml(msg.remitente || msg.from_address || '')}<br><b>Fecha:</b> ${escapeHtml(when)}<br>`
      + `<b>Asunto:</b> ${escapeHtml(msg.asunto || '')}<br><b>Para:</b> ${escapeHtml(msg.to_address || '')}`
      + `${msg.cc_address ? `<br><b>CC:</b> ${escapeHtml(msg.cc_address)}` : ''}</div><br>${originalBodyHtml(msg)}</div>`;
  };

  // Cambia el modo del editor (responder / responder a todos / reenviar) preparando
  // destinatarios y asunto. El borrador de la IA se conserva en "responder".
  const startReply = (mode) => {
    if (!detail) return;
    const sig = getSignature(replyAccountId);
    const subjectBase = detail.asunto || '';
    if (mode === 'forward') {
      setReplyTo('');
      setReplyCc('');
      setReplyBcc('');
      setShowReplyCc(false);
      setReplySubject(/^(fwd?|rv|tr):/i.test(subjectBase.trim()) ? subjectBase : `Fwd: ${subjectBase}`);
      setReplyDraft(sig ? `<p><br></p>${sig}` : '<p><br></p>');
      setReplyLastSignature(sig);
      setForwardAttachmentIdx((detail.attachments || []).filter((a) => a.downloadable && !a.inline).map((a) => a.index));
    } else {
      const from = detail.from_address || bareEmail(detail.remitente) || '';
      setReplyTo(from);
      if (mode === 'replyAll') {
        const others = [...splitAddresses(detail.to_address), ...splitAddresses(detail.cc_address)]
          .filter((addr) => {
            const e = bareEmail(addr);
            return e && e !== bareEmail(from) && !ownAddresses.has(e);
          });
        const unique = [];
        for (const addr of others) if (!unique.some((u) => bareEmail(u) === bareEmail(addr))) unique.push(addr);
        setReplyCc(unique.join(', '));
        setShowReplyCc(unique.length > 0);
      }
      if (replyMode === 'forward') {
        // Volviendo de "reenviar": recuperar el borrador inicial (propuesta IA + firma)
        setReplyDraft(replyDraftInitial);
        setReplySubject(replySubjectInitial);
        setReplyLastSignature(getSignature(replyAccountId));
      }
    }
    setReplyMode(mode);
  };

  // Cerrar el modal de detalle con la tecla Escape
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeDetail(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, replyAttachments]);

  const handleAction = async (type, ids = null) => {
    setLoadingActions((prev) => ({ ...prev, [type]: true }));
    try {
      const endpoint = type === 'poll' ? '/api/email/poll-now' : '/api/email/process-candidates';
      const res = await apiFetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: ids ? JSON.stringify({ ids }) : undefined
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error ejecutando acción');
      const msg = type === 'poll'
        ? 'Lectura de emails completada'
        : `IA: procesados ${data.processed || 0} / candidatos ${data.totalCandidates || 0}${data.reset ? ` (${data.reset} reprocesados)` : ''}${data.errors ? `, errores ${data.errors}` : ''}`;
      setError('');
      alert(msg);
      fetchData();
      fetchFolderCounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [type]: false }));
    }
  };

  const fetchScheduled = async (statusOverride, folderOverride) => {
    const targetFolder = folderOverride || folder;
    const status = targetFolder === 'drafts' ? 'draft' : ((statusOverride ?? scheduledStatus) || 'pending');
    try {
      const qs = new URLSearchParams();
      qs.append('status', status);
      qs.append('limit', '50');
      const res = await apiFetch(`/api/email/scheduled?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) setScheduledEmails(data);
      else if (!res.ok) setError((data && data.message) || 'Error obteniendo programados');
    } catch (err) {
      console.error(err);
      setError('Error de red obteniendo programados');
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await apiFetch('/api/email/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) setSavedTemplates(data);
      else if (!res.ok) setError((data && data.message) || 'Error obteniendo plantillas');
    } catch (err) {
      console.error(err);
      setError('Error de red obteniendo plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const formatDateTimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  };

  const nowPlusMinutes = (min = 10) => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + min);
    return formatDateTimeLocal(d.toISOString());
  };

  const formatSignature = (sig) => {
    if (!sig) return '';
    if (sig.includes('<')) return sig;
    return sig.replace(/\n/g, '<br>');
  };

  const getSignature = (accountId) => {
    const acc = emailAccounts.find((a) => String(a.id) === String(accountId));
    return formatSignature(acc?.signature || '');
  };

  const replaceSignature = (body, prevSig, nextSig) => {
    let base = body || '';
    if (prevSig && base.endsWith(prevSig)) {
      base = base.slice(0, base.length - prevSig.length);
    }
    return `${base}${nextSig || ''}`;
  };

  const textToHtml = (text) => (text || '').replace(/\n/g, '<br>');

  const stripHtml = (html) => {
    if (!html) return '';
    const withBreaks = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<p[^>]*>/gi, '');
    return withBreaks.replace(/<[^>]+>/g, '');
  };

  const templatePreview = (html) => {
    const text = stripHtml(html);
    if (text.length <= 80) return text;
    return `${text.slice(0, 80)}...`;
  };

  const parseAttachmentList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_err) {
        return [];
      }
    }
    return [];
  };

  const formatFileSize = (size) => {
    if (!size) return '';
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const attachmentPayload = (att) => ({
    storageName: att.storageName || att.id || att.filename,
    filename: att.originalName || att.filename || att.storageName || att.id,
    mimeType: att.mimeType,
    url: att.url,
    size: att.size
  });

  const scheduledAttachmentToCompose = (att) => ({
    storageName: att?.storageName || att?.path || att?.id || att?.filename,
    originalName: att?.filename || att?.originalName || att?.name || att?.storageName || 'adjunto',
    mimeType: att?.mimeType || att?.contentType || null,
    url: att?.url || null,
    size: att?.size || null,
    persisted: true
  });

  const cleanupUploadedAttachments = async (list) => {
    for (const att of list) {
      const key = att?.storageName || att?.id;
      if (!key) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await apiFetch(`/api/uploads/email/${encodeURIComponent(key)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 401) return handleUnauthorized();
      } catch (err) {
        console.error('No se pudo limpiar adjunto', err);
      }
    }
    return null;
  };

  const handleCloseCompose = async (skipCleanup = false) => {
    if (!skipCleanup && composeAttachments.length) {
      await cleanupUploadedAttachments(composeAttachments.filter((att) => !att.persisted));
    }
    setComposeAttachments([]);
    setCompose({ to: '', cc: '', bcc: '', subject: '', body: '', accountId: compose.accountId || '', sendAt: '', lastSignature: '' });
    setComposeEditingId(null);
    setComposeEditingStatus('');
    setSelectedTemplateId('');
    setComposeOpen(false);
    setComposeLoading(false);
    setComposeUploading(false);
  };

  const uploadComposeFiles = async (fileList) => {
    if (!fileList?.length) return;
    setComposeUploading(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        const res = await apiFetch('/api/uploads/email', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'No se pudo subir adjunto');
        setComposeAttachments((prev) => [...prev, data]);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setComposeUploading(false);
      if (composeFileInputRef.current) composeFileInputRef.current.value = '';
    }
  };

  const removeComposeAttachment = async (att) => {
    if (!att?.persisted) {
      await cleanupUploadedAttachments([att]);
    }
    setComposeAttachments((prev) => prev.filter((a) => (a.storageName || a.id) !== (att.storageName || att.id)));
  };

  const uploadReplyFiles = async (fileList) => {
    if (!fileList?.length) return;
    setReplyUploading(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        const res = await apiFetch('/api/uploads/email', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'No se pudo subir adjunto');
        setReplyAttachments((prev) => [...prev, data]);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setReplyUploading(false);
      if (replyFileInputRef.current) replyFileInputRef.current.value = '';
    }
  };

  const removeReplyAttachment = async (att) => {
    if (!att?.persisted) {
      await cleanupUploadedAttachments([att]);
    }
    setReplyAttachments((prev) => prev.filter((a) => (a.storageName || a.id) !== (att.storageName || att.id)));
  };

  const buildComposePayload = () => ({
    accountId: compose.accountId,
    to: compose.to,
    cc: compose.cc || undefined,
    bcc: compose.bcc || undefined,
    subject: compose.subject || '',
    body: stripHtml(compose.body),
    html: compose.body || '',
    attachments: composeAttachments.map((att) => attachmentPayload(att))
  });

  const parseComposeSendAt = () => {
    if (!compose.sendAt) return null;
    const sendAt = new Date(compose.sendAt);
    if (Number.isNaN(sendAt.getTime())) {
      throw new Error('Fecha/hora inválida');
    }
    return sendAt.toISOString();
  };

  const saveComposeScheduled = async (nextStatus) => {
    const payload = buildComposePayload();
    payload.status = nextStatus;
    const sendAtIso = parseComposeSendAt();
    if (sendAtIso) payload.sendAt = sendAtIso;
    const endpoint = composeEditingId ? `/api/email/scheduled/${composeEditingId}` : (nextStatus === 'draft' ? '/api/email/drafts' : '/api/email/schedule');
    const method = composeEditingId ? 'PUT' : 'POST';
    const res = await apiFetch(endpoint, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const data = await parseJsonSafe(res);
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error('Token inválido o expirado');
    }
    if (!res.ok) throw new Error(data.message || (nextStatus === 'draft' ? 'No se pudo guardar el borrador' : 'No se pudo programar'));
    return data;
  };

  const parseAiActions = (actionsRaw) => {
    if (!actionsRaw) return [];
    try {
      if (typeof actionsRaw === 'string') return JSON.parse(actionsRaw);
      if (Array.isArray(actionsRaw)) return actionsRaw;
    } catch (_e) {
      return [];
    }
    return [];
  };

  const executeAiActions = async () => {
    if (!detail) return;
    if (alreadyExecuted) {
      setModalMessage({ type: 'danger', text: 'Esta comunicación ya fue aprobada y ejecutada.' });
      return;
    }
    const actions = parseAiActions(detail.ai_actions);
    if (!actions.length && !detail.ai_suggested_body && !detail.ai_suggested_subject) {
      setModalMessage({ type: 'danger', text: 'No hay acciones que aprobar' });
      return;
    }
      const results = [];
      let meetLink = '';
      let hadError = false;
    setExecutingActions(true);
    setModalMessage(null);
    try {
      const attachments = [];
      const budgetsToMarkSent = [];

      for (const act of actions) {
        const type = (act?.type || '').toLowerCase();
        try {
        const checkConflict = async (startIso, endIso) => {
          if (!startIso) return false;
          const start = new Date(startIso);
          const end = endIso ? new Date(endIso) : new Date(start.getTime() + 60 * 60000);
          const res = await authFetch(`/api/calendar/events?from=${encodeURIComponent(start.toISOString())}&to=${encodeURIComponent(end.toISOString())}`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await parseJsonSafe(res);
          if (!res.ok) return false;
          return Array.isArray(data) && data.some((ev) => {
            const evStart = new Date(ev.start);
            const evEnd = new Date(ev.end);
            return start < evEnd && end > evStart;
          });
        };
        if (type === 'create_task') {
          const impRaw = String(act.importance || '').toLowerCase();
          const payload = {
            titulo: act.title || 'Tarea generada por IA',
            comentarios: act.comments || '',
            importance: ['alta', 'media', 'baja'].includes(impRaw) ? impRaw : 'media',
            estado: 'pendiente'
          };
          if (detail.client_id) payload.client_id = detail.client_id;
          if (detail.id) payload.communication_id = detail.id;
          if (defaultTaskUserId) payload.user_id = Number(defaultTaskUserId);
          const res = await authFetch('/api/tasks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload)
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo crear tarea');
          results.push('Tarea creada');
        }
        if (type === 'create_event') {
          const startIso = act.date && act.time ? new Date(`${act.date}T${act.time}`).toISOString() : null;
          const endIso = startIso ? new Date(new Date(startIso).getTime() + (act.durationMin || 30) * 60000).toISOString() : null;
          const conflict = await checkConflict(startIso, endIso);
          if (conflict) {
            results.push('Evento no creado por conflicto de agenda');
            continue;
          }
          const res = await authFetch('/api/calendar/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              date: act.date,
              time: act.time,
              durationMin: act.durationMin || 30,
              summary: act.summary || detail.asunto || 'Reunión',
              description: act.description || '',
              attendeeEmail: detail.from_address || detail.remitente,
              // Sin esto Google no genera el enlace de Meet y el cuerpo prometía videollamada
              addConference: true
            })
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo crear evento');
          if (!meetLink && data.hangoutLink) meetLink = data.hangoutLink;
          results.push(`Evento creado${data.hangoutLink ? ` (Meet: ${data.hangoutLink})` : ''}`);
        }
        if (type === 'update_event') {
          const startIso = act.start || (act.date && act.time ? new Date(`${act.date}T${act.time}`).toISOString() : null);
          const endIso = act.end || (startIso ? new Date(new Date(startIso).getTime() + (act.durationMin || 30) * 60000).toISOString() : null);
          const conflict = await checkConflict(startIso, endIso);
          if (conflict) {
            results.push('Evento no actualizado por conflicto de agenda');
            continue;
          }
          const res = await authFetch(`/api/calendar/events/${act.eventId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              summary: act.summary,
              description: act.description,
              start: startIso,
              end: endIso,
              calendarId: act.calendarId
            })
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo actualizar evento');
          results.push('Evento actualizado');
        }
        if (type === 'delete_event') {
          const res = await authFetch(`/api/calendar/events/${act.eventId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ calendarId: act.calendarId })
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo eliminar evento');
          results.push('Evento eliminado');
        }
        if (type === 'send_invoices' && detail.dolibarr_thirdparty_id) {
          const body = {
            thirdpartyId: detail.dolibarr_thirdparty_id,
            refs: act.refs || [],
            limit: act.last || 2,
            year: act.year
          };
          const res = await authFetch('/api/invoices/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body)
          });
          const data = await parseJsonSafe(res);
          if (res.ok && Array.isArray(data.attachments)) {
            attachments.push(...data.attachments);
            results.push(`Facturas adjuntas (${data.count || data.attachments.length})`);
          }
        }
        if (type === 'create_dev_request') {
          if (!detail.client_id) throw new Error('El remitente no está identificado como cliente: no se puede crear el ajuste');
          // Los adjuntos del correo se vinculan solos en el backend (source_message_id).
          const res = await authFetch('/api/dev-requests', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              source: 'email',
              source_message_id: detail.id,
              client_id: detail.client_id,
              title: act.title || detail.asunto || 'Ajuste solicitado',
              description: act.description || act.comments || '',
              priority: act.priority,
              project: act.project
            })
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo crear el ajuste');
          results.push(`Cambio #${data.id} creado${data.project_title ? '' : ' (elige el proyecto en Cambios)'}`);
        }
        if (type === 'create_support_ticket') {
          if (!detail.client_id) throw new Error('El remitente no está identificado como cliente: no se puede abrir el ticket');
          const res = await authFetch('/api/support', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              client_id: detail.client_id,
              subject: act.subject || act.title || detail.asunto || 'Incidencia',
              body: act.body || act.comments || '',
              priority: act.priority || 'normal'
            })
          });
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'No se pudo crear el ticket de soporte');
          results.push(`Ticket de soporte #${data.id} creado`);
        }
        if (type === 'send_budget' && act.budgetId) {
          const res = await authFetch(`/api/clients/budgets/${act.budgetId}/attachment-payload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
          });
          const data = await parseJsonSafe(res);
          if (!res.ok || !Array.isArray(data.attachments)) throw new Error(data.message || 'No se pudo adjuntar el presupuesto');
          attachments.push(...data.attachments);
          budgetsToMarkSent.push(act.budgetId);
          results.push(`Presupuesto ${act.numero || act.budgetId} adjunto`);
        }
        } catch (actErr) {
          if (type === 'send_budget') {
            // Sin el presupuesto adjunto no se puede enviar un email que dice adjuntarlo:
            // se aborta la aprobación entera (queda reintentable, no se registra ejecución).
            throw new Error(`No se pudo adjuntar el presupuesto: ${actErr.message}. No se ha enviado nada; vuelve a intentarlo.`);
          }
          hadError = true;
          results.push(`Error en acción ${type || 'desconocida'}: ${actErr.message}`);
        }
      }

      // Si no hay acción send_invoices pero el intent es facturas, intenta adjuntar últimas 2 facturas
      if (!attachments.length && detail.ai_intent === 'facturas' && detail.dolibarr_thirdparty_id) {
        const res = await authFetch('/api/invoices/download', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ thirdpartyId: detail.dolibarr_thirdparty_id, limit: 2 })
        });
        const data = await parseJsonSafe(res);
        if (res.ok && Array.isArray(data.attachments)) {
          attachments.push(...data.attachments);
          results.push(`Facturas adjuntas (${data.count || data.attachments.length})`);
        }
      }

      const isSpam = (detail.ai_intent || '').toLowerCase() === 'spam' || (detail.ai_decision_final || '').toLowerCase() === 'spam';
      const hasDraft = Boolean(detail.ai_suggested_body || detail.ai_suggested_subject);
      let sentFinalBody = null;
      let sentFinalSubject = null;
      if (isSpam) {
        results.push('Omitido: marcado como spam, no se envía respuesta');
      } else if (hasDraft) {
        try {
        const targetAccountId = replyAccountId || emailAccounts[0]?.id;
        if (!targetAccountId) throw new Error('No hay cuenta configurada para enviar');
        const sig = getSignature(targetAccountId);
        const replaceMeet = (txt, link) => {
          if (!txt) return txt;
          if (!link) return txt;
          const patterns = [
            /\[enlace de videollamada\]/gi,
            /\[enlace videollamada\]/gi,
            /\[enlace reunión\]/gi,
            /\[enlace reunion\]/gi,
            /\[enlace llamada\]/gi
          ];
          let replaced = txt;
          let didReplace = false;
          patterns.forEach((re) => {
            if (re.test(replaced)) {
              replaced = replaced.replace(re, link);
              didReplace = true;
            }
          });
          if (!didReplace) {
            replaced = `${replaced}\n\nEnlace de videollamada: ${link}`;
          }
          return replaced;
        };
        // Prioridad del cuerpo a enviar: (1) edición explícita del borrador (approveEdit),
        // (2) el editor "Responder" si el usuario lo modificó, (3) la propuesta de la IA.
        // Nunca debe salir el texto de la IA si el usuario escribió su propia versión.
        const replyWasEdited = (replyMode === 'reply' || replyMode === 'replyAll') && (replyDraft !== replyDraftInitial || replySubject !== replySubjectInitial);
        let htmlBody;
        let subjectToSend;
        let feedbackBody;
        if (approveEdit?.body != null) {
          const draftBody = replaceMeet(approveEdit.body, meetLink);
          htmlBody = (draftBody ? textToHtml(draftBody) : '') + (sig || '');
          const s = approveEdit.subject != null ? approveEdit.subject : (detail.ai_suggested_subject || detail.asunto || '');
          subjectToSend = s && !s.toLowerCase().startsWith('re:') ? `Re: ${s}` : s;
          feedbackBody = approveEdit.body;
        } else if (replyWasEdited) {
          htmlBody = replaceMeet(replyDraft, meetLink); // ya incluye la firma del editor
          subjectToSend = replySubject;
          feedbackBody = stripHtml(replyLastSignature ? replyDraft.replace(replyLastSignature, '') : replyDraft);
        } else {
          const draftBody = replaceMeet(detail.ai_suggested_body, meetLink);
          htmlBody = (draftBody ? textToHtml(draftBody) : '') + (sig || '');
          const s = detail.ai_suggested_subject || detail.asunto || '';
          subjectToSend = s && !s.toLowerCase().startsWith('re:') ? `Re: ${s}` : s;
          feedbackBody = detail.ai_suggested_body || null;
        }
        const res = await authFetch('/api/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            accountId: targetAccountId,
            to: detail.from_address || detail.remitente || detail.destinatario,
            subject: subjectToSend,
            body: stripHtml(htmlBody),
            html: htmlBody,
            attachments: attachments.length ? attachments : undefined,
            replyToMessageId: detail.id || undefined
          })
        });
        const data = await parseJsonSafe(res);
        if (!res.ok) throw new Error(data.message || 'No se pudo enviar email');
        sentFinalBody = feedbackBody;
        sentFinalSubject = subjectToSend;
        results.push('Email enviado');
        // El presupuesto adjunto ya ha salido por email: pasa a Enviado.
        for (const budgetId of budgetsToMarkSent) {
          try {
            const msRes = await authFetch(`/api/clients/budgets/${budgetId}/mark-sent`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!msRes.ok) {
              const msData = await parseJsonSafe(msRes);
              throw new Error(msData.message || `HTTP ${msRes.status}`);
            }
          } catch (msErr) {
            // El email ya salió: no se aborta, pero queda traza para revisarlo a mano.
            hadError = true;
            results.push(`Email enviado pero el presupuesto sigue en Borrador (marcar Enviado falló: ${msErr.message})`);
          }
        }
        } catch (sendErr) {
          hadError = true;
          results.push(`Error al enviar email: ${sendErr.message}`);
        }
      } else {
        results.push('Sin borrador: no se envía email');
      }

      // Registramos SIEMPRE la ejecución (aunque alguna acción fallara) para no recrear
      // tareas/eventos al reintentar. Si hubo errores, dejamos pendiente de revisión.
      const logRes = await authFetch(`/api/communications/${detail.id}/approve-execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          actions,
          results,
          notes: results.join(', ') || 'Ejecutado',
          estado: hadError ? 'pendiente_revision' : 'auto_resuelto',
          // Texto realmente enviado (editado o no): el agente aprende de tus correcciones
          finalSubject: sentFinalSubject,
          finalBody: sentFinalBody
        })
      });
      const logData = await parseJsonSafe(logRes);
      if (logRes.status === 401) return handleUnauthorized();
      if (!logRes.ok) throw new Error(logData.message || 'No se pudo actualizar el estado');

      setModalMessage({
        type: hadError ? 'danger' : 'success',
        text: hadError
          ? `Ejecutado con incidencias: ${results.join(', ')}`
          : `Acciones ejecutadas: ${results.join(', ') || 'ninguna'}`
      });
      setDetail(logData);
      setReplyMode(null);
      setApproveEdit(null);
      fetchData();
      refreshDetail(detail.id);
    } catch (err) {
      setModalMessage({ type: 'danger', text: `Error al ejecutar: ${err.message}` });
    } finally {
      setExecutingActions(false);
    }
  };

  const openComposeForNew = (prefill = null) => {
    const defaultAccount = compose.accountId || emailAccounts[0]?.id || '';
    const sig = getSignature(defaultAccount);
    const baseBody = prefill ? textToHtml(prefill.body || '') : '';
    setCompose({
      to: prefill?.to || '',
      cc: '',
      bcc: '',
      subject: prefill?.subject || '',
      body: `${baseBody}${sig || '<p></p>'}`,
      lastSignature: sig,
      accountId: defaultAccount,
      sendAt: nowPlusMinutes(10)
    });
    setComposeEditingId(null);
    setComposeEditingStatus('');
    setComposeAttachments([]);
    setSelectedTemplateId('');
    setComposeOpen(true);
  };

  const openComposeForScheduled = (mail) => {
    const targetAccountId = mail?.account_id || compose.accountId || emailAccounts[0]?.id || '';
    const sig = getSignature(targetAccountId);
    const rawAttachments = parseAttachmentList(mail?.attachments);
    setCompose({
      to: mail?.to_address || '',
      cc: mail?.cc || '',
      bcc: mail?.bcc || '',
      subject: mail?.subject || '',
      body: mail?.html || textToHtml(mail?.body || ''),
      lastSignature: sig,
      accountId: targetAccountId,
      sendAt: formatDateTimeLocal(mail?.send_at)
    });
    setComposeEditingId(mail?.id || null);
    setComposeEditingStatus(mail?.status || '');
    setComposeAttachments(rawAttachments.map((att) => scheduledAttachmentToCompose(att)));
    setSelectedTemplateId('');
    setComposeOpen(true);
  };

  // «Enviar ahora» desde la lista (borrador o programado): el backend lo envía y lo marca enviado
  const sendScheduledNowFromList = async (mail) => {
    if (!mail?.id) return;
    if (!mail.to_address) return alert('Este borrador no tiene destinatario: edítalo antes de enviarlo');
    if (!window.confirm(`¿Enviar ahora el email «${mail.subject || '(sin asunto)'}» a ${mail.to_address}?`)) return;
    setSendingNowId(mail.id);
    try {
      const res = await apiFetch(`/api/email/scheduled/${mail.id}/send-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
      fetchScheduled();
      fetchFolderCounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingNowId(null);
    }
  };

  const cancelScheduledEmail = async (mail) => {
    if (!mail?.id) return;
    const confirmMessage = mail.status === 'draft' ? '¿Eliminar borrador?' : (mail.status === 'error' ? '¿Descartar este envío fallido?' : '¿Cancelar envío programado?');
    if (!window.confirm(confirmMessage)) return;
    try {
      const res = await apiFetch(`/api/email/scheduled/${mail.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo cancelar');
      fetchScheduled();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (prefillApplied.current) return;
    const statePrefill = location.state && location.state.composePrefill ? location.state.composePrefill : null;
    const params = new URLSearchParams(location.search || '');
    const queryPrefill = params.get('compose') ? {
      to: params.get('to') || '',
      subject: params.get('subject') || '',
      body: params.get('body') || ''
    } : null;
    const prefill = statePrefill || queryPrefill;
    if (prefill) {
      prefillApplied.current = true;
      openComposeForNew(prefill);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, emailAccounts, compose.accountId]);

  useEffect(() => {
    // Ajustar firmas cuando se cargan las cuentas
    if (composeOpen && compose.accountId && compose.lastSignature === '') {
      const sig = getSignature(compose.accountId);
      setCompose((p) => ({
        ...p,
        body: replaceSignature(p.body, p.lastSignature, sig || '<p><br></p>'),
        lastSignature: sig
      }));
    }
    if (detail && replyAccountId && replyLastSignature === '') {
      const sig = getSignature(replyAccountId);
      setReplyDraft((prev) => replaceSignature(prev, replyLastSignature, sig));
      setReplyLastSignature(sig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAccounts, composeOpen, compose.accountId, compose.lastSignature, detail, replyAccountId, replyLastSignature]);

  const parsedActions = useMemo(() => parseAiActions(detail?.ai_actions), [detail]);
  const executedActions = useMemo(() => parseAiActions(detail?.ai_executed_actions), [detail]);
  const alreadyExecuted = Boolean(detail?.ai_executed_at || detail?.estado === 'auto_resuelto');
  const isWhatsapp = detail?.canal === 'whatsapp';
  // Un asunto suelto sin cuerpo no es un borrador: solo hay algo que aprobar si hay cuerpo o acciones
  const hasReplyOrActions = Boolean(detail?.ai_suggested_body || (parsedActions && parsedActions.length));
  // El botón "Aprobar y ejecutar" SOLO tiene sentido sobre correo ENTRANTE gestionado
  // por IA con propuesta pendiente: nunca sobre notas manuales de seguimiento ni salientes.
  const isManualNote = Boolean(detail?.meta && String(detail.meta.manual) === 'true');
  const canShowApprove = detail?.canal === 'email' && detail?.direccion === 'in' && !isManualNote && hasReplyOrActions && !alreadyExecuted && !detail?.deleted_at && detail?.estado !== 'spam';
  // ¿La IA ha visto este correo? (si no, la tarjeta ofrece procesarlo en vez de un vacío confuso)
  const aiHasRun = Boolean(detail?.ai_last_run_at || detail?.ai_intent);
  const aiNoReplyNeeded = aiHasRun && !alreadyExecuted && !hasReplyOrActions && detail?.ai_decision_final === 'resuelto';
  const aiDerivedToComercial = aiHasRun && !alreadyExecuted && !hasReplyOrActions && String(detail?.ai_intent || '').toLowerCase() === 'derivar_comercial';
  // Qué hará "Aprobar": enviar el borrador y/o ejecutar acciones (etiqueta y confirmación coherentes)
  const approveLabel = detail?.ai_suggested_body && parsedActions.length
    ? 'Aprobar: enviar respuesta y ejecutar acciones'
    : (detail?.ai_suggested_body ? 'Aprobar y enviar respuesta' : 'Aprobar y ejecutar acciones');
  // El editor está en modo respuesta (no reenvío): solo entonces su texto sustituye al borrador de la IA al aprobar
  const editorIsReply = replyMode === 'reply' || replyMode === 'replyAll';
  const replyEditedInEditor = editorIsReply && (replyDraft !== replyDraftInitial || replySubject !== replySubjectInitial);

  // La agrupación por hilo ahora se hace en el backend (una fila por hilo con _count).
  // items ya viene agrupado cuando groupByThread está activo.

  // Volver a pasar el correo por la IA (tarjeta y pie del modal)
  const reprocessWithAi = async () => {
    if (!detail) return;
    setReprocessing(true);
    setModalMessage(null);
    try {
      const res = await apiFetch(`/api/communications/${detail.id}/reprocess-ia`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo reprocesar');
      setDetail(data);
      setApproveEdit(null);
      setReplyMode(null);
      setModalMessage({ type: 'success', text: 'Reprocesado con IA.' });
      fetchData();
    } catch (err) {
      setModalMessage({ type: 'danger', text: err.message });
    } finally {
      setReprocessing(false);
    }
    return null;
  };

  // Envío desde el editor del detalle (responder / responder a todos / reenviar)
  const sendFromEditor = async () => {
    if (!detail) return;
    if (!replyAccountId) { setModalMessage({ type: 'danger', text: 'Selecciona una cuenta' }); return; }
    if (!String(replyTo || '').trim()) { setModalMessage({ type: 'danger', text: 'Indica al menos un destinatario' }); return; }
    const isForward = replyMode === 'forward';
    if (!isForward && (detail.estado === 'auto_resuelto' || detail.replied_at)
      && !window.confirm('Ya se envió una respuesta a este email. ¿Seguro que quieres enviar otra respuesta?')) return;
    setSendingReply(true);
    setModalMessage(null);
    try {
      const extra = isForward ? buildForwardHtml(detail) : (includeQuote ? buildQuoteHtml(detail) : '');
      const html = `${replyDraft || ''}${extra}`;
      const payload = {
        accountId: replyAccountId,
        to: replyTo,
        cc: replyCc || undefined,
        bcc: replyBcc || undefined,
        subject: replySubject || detail.asunto || '',
        body: stripHtml(html),
        html,
        attachments: replyAttachments.map((att) => attachmentPayload(att))
      };
      if (isForward) {
        payload.forwardOfMessageId = detail.id;
        payload.originalAttachmentIndexes = forwardAttachmentIdx;
      } else {
        payload.replyToMessageId = detail.id;
      }
      const res = await apiFetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
      await cleanupUploadedAttachments(replyAttachments.filter((att) => !att.persisted));
      setReplyAttachments([]);
      setModalMessage({ type: 'success', text: isForward ? 'Correo reenviado correctamente.' : 'Respuesta enviada correctamente.' });
      setReplyMode(null);
      await refreshDetail(detail.id);
      fetchData();
    } catch (err) {
      setModalMessage({ type: 'danger', text: err.message });
    } finally {
      setSendingReply(false);
    }
    return null;
  };

  const folderBadge = (key) => {
    const c = folderCounts || {};
    if (key === 'inbox' && c.inbox_unread > 0) return <span className="badge bg-primary ms-auto">{c.inbox_unread}</span>;
    if (key === 'pending' && c.pending > 0) return <span className="badge bg-warning text-dark ms-auto">{c.pending}</span>;
    if (key === 'sent' && c.sent_bounced > 0) return <span className="badge bg-danger ms-auto" title="Envíos rebotados">{c.sent_bounced}</span>;
    if (key === 'spam' && c.spam > 0) return <span className="badge bg-light text-muted border ms-auto">{c.spam}</span>;
    if (key === 'trash' && c.trash > 0) return <span className="badge bg-light text-muted border ms-auto">{c.trash}</span>;
    return null;
  };

  // Acciones en bloque disponibles según la carpeta
  const bulkButtons = () => {
    const ids = selectedIds;
    const btn = (label, action, cls = 'btn-outline-secondary', opts = {}) => (
      <button key={action} className={`btn btn-sm ${cls}`} disabled={bulkBusy} onClick={() => bulkAction(action, ids, opts)}>{label}</button>
    );
    const readBtns = [
      <button key="read" className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => markRead(ids, true)}>Marcar leído</button>,
      <button key="unread" className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => markRead(ids, false)}>Marcar no leído</button>
    ];
    switch (folder) {
      case 'inbox':
      case 'pending':
        return [
          <button key="ia" className="btn btn-sm btn-outline-primary" disabled={loadingActions.processSelected} title="Procesa con la IA los seleccionados; si ya tenían borrador, se reprocesan" onClick={() => handleAction('processSelected', ids)}>
            {loadingActions.processSelected ? 'Procesando...' : 'Procesar con IA'}
          </button>,
          ...readBtns,
          btn(<><BsArchive className="me-1" />Archivar</>, 'archive'),
          btn(<><BsShieldX className="me-1" />Descartar</>, 'spam'),
          btn(<><BsTrash className="me-1" />Papelera</>, 'trash', 'btn-outline-danger')
        ];
      case 'archived':
        return [...readBtns, btn('Devolver a Recibidos', 'unarchive'), btn(<><BsTrash className="me-1" />Papelera</>, 'trash', 'btn-outline-danger')];
      case 'sent':
        return [btn(<><BsTrash className="me-1" />Papelera</>, 'trash', 'btn-outline-danger')];
      case 'spam':
        return [
          btn('No es spam: recuperar', 'not_spam', 'btn-outline-success'),
          btn('Eliminar definitivamente', 'delete', 'btn-danger', { confirmText: `¿Eliminar definitivamente ${ids.length} mensaje(s)? Esta acción no se puede deshacer.` })
        ];
      case 'trash':
        return [
          btn(<><BsArrowCounterclockwise className="me-1" />Restaurar</>, 'restore', 'btn-outline-success'),
          btn('Eliminar definitivamente', 'delete', 'btn-danger', { confirmText: `¿Eliminar definitivamente ${ids.length} mensaje(s)? Esta acción no se puede deshacer.` })
        ];
      default:
        return [];
    }
  };

  const actionButtons = (fullWidth = false) => {
    const buttonClass = (base) => `${base}${fullWidth ? ' w-100' : ''}`;
    return (
      <>
        <button className={buttonClass('btn btn-primary')} onClick={() => {
          openComposeForNew();
          setActionsMenuOpen(false);
        }}>
          Redactar
        </button>
        <button className={buttonClass('btn btn-outline-secondary')} disabled={loadingActions.poll} onClick={() => { handleAction('poll'); setActionsMenuOpen(false); }}>
          {loadingActions.poll ? 'Leyendo emails...' : 'Leer emails ahora'}
        </button>
        <button className={buttonClass('btn btn-outline-primary')} disabled={loadingActions.process} onClick={() => { handleAction('process'); setActionsMenuOpen(false); }}>
          {loadingActions.process ? 'Procesando IA...' : 'Procesar candidatos IA'}
        </button>
        <button className={buttonClass('btn btn-outline-secondary')} onClick={async () => { await fetchNanoLogs(); setShowLogs(true); setActionsMenuOpen(false); }}>
          Log
        </button>
        <button className={buttonClass('btn btn-outline-secondary d-flex align-items-center gap-1 justify-content-center')} onClick={() => { setShowConfig(true); setActionsMenuOpen(false); }}>
          <BsGear /> Configuración
        </button>
      </>
    );
  };

  return (
    <div className="small" style={{ fontSize: '0.9rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Correo</h2>
        {isMobile ? (
          <div className="position-relative">
            <button className="btn btn-outline-secondary" onClick={() => setActionsMenuOpen((o) => !o)}>
              <BsThreeDotsVertical />
            </button>
            {actionsMenuOpen && (
              <div className="dropdown-menu dropdown-menu-end show" style={{ right: 0, left: 'auto', minWidth: '220px' }}>
                {actionButtons(true)}
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex flex-wrap justify-content-end align-items-center gap-2" style={{ rowGap: '0.5rem' }}>
            {actionButtons(false)}
          </div>
        )}
      </div>

      <div className="row g-3">
        <div className={isMobile ? 'col-12' : 'col-md-2'}>
          {isMobile ? (
            <div className="d-flex gap-1 overflow-auto pb-1" style={{ whiteSpace: 'nowrap' }}>
              {FOLDERS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm ${folder === key ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center gap-1`}
                  onClick={() => changeFolder(key)}
                >
                  <Icon /> {label}{folderBadge(key)}
                </button>
              ))}
            </div>
          ) : (
            <div className="list-group shadow-sm">
              {FOLDERS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 ${folder === key ? 'active' : ''}`}
                  onClick={() => changeFolder(key)}
                >
                  <Icon className="flex-shrink-0" /> <span className="text-truncate">{label}</span>{folderBadge(key)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={isMobile ? 'col-12' : 'col-md-10'}>
      {isMessageFolder && (
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-5">
              <input className="form-control" placeholder="Buscar en asunto, cuerpo o remitente..." value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}>
                <option value="">Todos los estados</option>
                {statusOptions.map((st) => <option key={st} value={st}>{statusLabel(st)}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={accountFilter} onChange={(e) => { setPage(1); setAccountFilter(e.target.value); }}>
                <option value="">Todas las cuentas</option>
                {emailAccounts.map((acc) => (
                  <option key={acc.id} value={acc.imap_user || acc.from_email}>{acc.from_email || acc.imap_user}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex gap-1">
              <button
                className={`btn ${showAdvanced ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1`}
                onClick={() => setShowAdvanced(!showAdvanced)}
                title="Búsqueda avanzada"
              >
                <BsGear className="me-1" />Filtros
              </button>
              {hasActiveFilters && (
                <button className="btn btn-outline-danger" onClick={clearAllFilters} title="Limpiar filtros">✕</button>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="d-flex flex-wrap gap-1 mt-2">
              {fromFilter && (
                <span className="badge bg-info text-dark">De: {fromFilter} <span role="button" onClick={() => setFromFilter('')}>✕</span></span>
              )}
              {toFilter && (
                <span className="badge bg-info text-dark">Para: {toFilter} <span role="button" onClick={() => setToFilter('')}>✕</span></span>
              )}
              {dateFromFilter && (
                <span className="badge bg-info text-dark">Desde: {dateFromFilter} <span role="button" onClick={() => setDateFromFilter('')}>✕</span></span>
              )}
              {dateToFilter && (
                <span className="badge bg-info text-dark">Hasta: {dateToFilter} <span role="button" onClick={() => setDateToFilter('')}>✕</span></span>
              )}
              {directionFilter && (
                <span className="badge bg-info text-dark">{directionFilter === 'in' ? 'Recibidos' : 'Enviados'} <span role="button" onClick={() => setDirectionFilter('')}>✕</span></span>
              )}
              {accountFilter && (
                <span className="badge bg-info text-dark">Cuenta: {accountFilter} <span role="button" onClick={() => setAccountFilter('')}>✕</span></span>
              )}
            </div>
          )}

          {showAdvanced && (
            <div className="row g-2 mt-2 pt-2 border-top">
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">De (remitente)</label>
                <input className="form-control form-control-sm" placeholder="email@ejemplo.com" value={fromFilter} onChange={(e) => { setPage(1); setFromFilter(e.target.value); }} />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Para / CC (destinatario)</label>
                <input className="form-control form-control-sm" placeholder="email@ejemplo.com" value={toFilter} onChange={(e) => { setPage(1); setToFilter(e.target.value); }} />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Fecha desde</label>
                <input type="date" className="form-control form-control-sm" value={dateFromFilter} onChange={(e) => { setPage(1); setDateFromFilter(e.target.value); }} />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Fecha hasta</label>
                <input type="date" className="form-control form-control-sm" value={dateToFilter} onChange={(e) => { setPage(1); setDateToFilter(e.target.value); }} />
              </div>
            </div>
          )}

          <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
            <div className="form-check form-check-inline m-0">
              <input className="form-check-input" type="checkbox" id="groupThread" checked={groupByThread}
                onChange={(e) => { setGroupByThread(e.target.checked); setSelectedIds([]); setSelectAll(false); setPage(1); }} />
              <label htmlFor="groupThread" className="form-check-label">Agrupar por conversación</label>
            </div>
            {folder !== 'sent' && (
              <div className="form-check form-check-inline m-0">
                <input className="form-check-input" type="checkbox" id="unreadOnly" checked={unreadOnly}
                  onChange={(e) => { setUnreadOnly(e.target.checked); setPage(1); }} />
                <label htmlFor="unreadOnly" className="form-check-label">Solo no leídos</label>
              </div>
            )}
            <div className="form-check form-check-inline m-0">
              <input className="form-check-input" type="checkbox" id="attachmentsOnly" checked={attachmentsOnly}
                onChange={(e) => { setAttachmentsOnly(e.target.checked); setPage(1); }} />
              <label htmlFor="attachmentsOnly" className="form-check-label"><BsPaperclip /> Con adjuntos</label>
            </div>
            {groupByThread && <span className="text-muted small">(selección múltiple deshabilitada)</span>}
          </div>
        </div>
      </div>
      )}

        {error && <div className="alert alert-danger">{error}</div>}
        {execMessage && <div className="alert alert-info">{execMessage}</div>}
        {loadingDetail && !detail && <div className="alert alert-info">Cargando detalle...</div>}

      {(folder === 'scheduled' || folder === 'drafts') && (
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h6 className="mb-0">{folder === 'drafts' ? 'Borradores' : 'Envíos programados'}</h6>
              <div className="small text-muted">
                {folder === 'drafts'
                  ? 'Emails guardados sin enviar. Edítalos o envíalos cuando quieras.'
                  : 'Emails con fecha de envío. Se mandan solos a la hora indicada; también puedes enviarlos ya.'}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {folder === 'scheduled' && (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 180 }}
                  value={scheduledStatus}
                  onChange={(e) => {
                    const next = e.target.value;
                    setScheduledStatus(next);
                    fetchScheduled(next);
                  }}
                >
                  <option value="pending">Pendientes de envío</option>
                  <option value="sent">Enviados</option>
                  <option value="error">Con error</option>
                  <option value="cancelled">Cancelados</option>
                  <option value="all">Todos</option>
                </select>
              )}
              <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchScheduled()}>Refrescar</button>
              {folder === 'drafts' && (
                <button className="btn btn-sm btn-primary" onClick={() => openComposeForNew()}>Nuevo borrador</button>
              )}
            </div>
          </div>
          {visibleScheduled.length ? (
            <>
            {/* Móvil: tarjetas (la tabla obligaba a desplazarse para ver los botones) */}
            <div className="d-md-none">
              {visibleScheduled.map((m) => (
                <div key={`card-${m.id}`} className="border rounded p-2 mb-2">
                  <div className="fw-semibold">{m.subject || '(sin asunto)'}</div>
                  <div className="small">{m.to_address || <span className="text-muted">(sin destinatario)</span>}</div>
                  <div className="small text-muted">
                    {folder === 'drafts'
                      ? `Guardado ${m.updated_at ? new Date(m.updated_at).toLocaleString() : ''}`
                      : `${scheduledStatusLabel(m.status)}${m.send_at ? ` · ${new Date(m.send_at).toLocaleString()}` : ''}`}
                    {m.status === 'error' && m.error_text ? ` · ${m.error_text}` : ''}
                  </div>
                  {['pending', 'draft', 'error'].includes(m.status) && (
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-primary" disabled={sendingNowId === m.id} onClick={() => sendScheduledNowFromList(m)}>
                        {sendingNowId === m.id ? 'Enviando…' : 'Enviar ahora'}
                      </button>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openComposeForScheduled(m)}>Editar</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => cancelScheduledEmail(m)}>{m.status === 'draft' ? 'Eliminar' : (m.status === 'error' ? 'Descartar' : 'Cancelar')}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="table-responsive d-none d-md-block">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Para</th>
                    <th>Asunto</th>
                    <th>Cuenta</th>
                    <th>{folder === 'drafts' ? 'Guardado' : 'Enviar el'}</th>
                    {folder === 'scheduled' && <th>Estado</th>}
                    {folder === 'scheduled' && <th>Resultado</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleScheduled.map((m) => (
                    <tr key={m.id}>
                      <td>{m.to_address || <span className="text-muted">(sin destinatario)</span>}</td>
                      <td>{m.subject || '-'}</td>
                      <td>{m.from_name ? `${m.from_name} <${m.from_email}>` : (m.from_email || '-')}</td>
                      <td>
                        {folder === 'drafts'
                          ? (m.updated_at ? new Date(m.updated_at).toLocaleString() : '-')
                          : (m.send_at ? new Date(m.send_at).toLocaleString() : '-')}
                      </td>
                      {folder === 'scheduled' && <td>{scheduledStatusLabel(m.status)}</td>}
                      {folder === 'scheduled' && (
                        <td className="text-muted small">
                          {m.status === 'sent' && m.sent_at ? `Enviado: ${new Date(m.sent_at).toLocaleString()}` : ''}
                          {m.status === 'error' ? `Error: ${m.error_text || 'Error'}` : ''}
                          {m.status === 'pending' || m.status === 'cancelled' || m.status === 'sending' ? '-' : ''}
                        </td>
                      )}
                      <td className="text-end">
                        <div className="d-inline-flex justify-content-end gap-2">
                          {['pending', 'draft', 'error'].includes(m.status) && (
                            <button
                              className="btn btn-sm btn-primary"
                              disabled={sendingNowId === m.id}
                              onClick={() => sendScheduledNowFromList(m)}
                            >
                              {sendingNowId === m.id ? 'Enviando…' : 'Enviar ahora'}
                            </button>
                          )}
                          {['pending', 'draft', 'error'].includes(m.status) && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openComposeForScheduled(m)}
                            >
                              Editar
                            </button>
                          )}
                          {['pending', 'draft', 'error'].includes(m.status) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => cancelScheduledEmail(m)}
                            >
                              {m.status === 'draft' ? 'Eliminar' : (m.status === 'error' ? 'Descartar' : 'Cancelar')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          ) : (
            <div className="text-muted small">{folder === 'drafts' ? 'No hay borradores.' : 'No hay envíos para este filtro.'}</div>
          )}
        </div>
      </div>

      )}

      {folder === 'templates' && (
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Plantillas guardadas</h6>
            <button className="btn btn-sm btn-outline-secondary" onClick={fetchTemplates}>Refrescar</button>
          </div>
          {loadingTemplates ? (
            <div className="text-muted small">Cargando plantillas...</div>
          ) : savedTemplates.length ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Preview</th>
                    <th>Actualizada</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {savedTemplates.map((tpl) => (
                    <tr key={tpl.id}>
                      <td>{tpl.name}</td>
                      <td className="text-muted">{templatePreview(tpl.body_html)}</td>
                      <td>{tpl.updated_at ? new Date(tpl.updated_at).toLocaleString() : '-'}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => {
                            setTemplateForm({ id: tpl.id, name: tpl.name || '', body: tpl.body_html || '' });
                            setTemplateModalOpen(true);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={async () => {
                            if (!window.confirm('Eliminar plantilla?')) return;
                            try {
                              const res = await apiFetch(`/api/email/templates/${tpl.id}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              const data = await parseJsonSafe(res);
                              if (res.status === 401) return handleUnauthorized();
                              if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
                              fetchTemplates();
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-muted small">No hay plantillas guardadas.</div>
          )}
        </div>
      </div>

      )}

      {isMessageFolder && (
      <div className="card shadow-sm">
        <div className="card-body small">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h6 className="mb-0 d-flex align-items-center gap-2">
              {folderLabel(folder)}
              {folder === 'inbox' && folderCounts.inbox_unread > 0 && <span className="badge bg-primary">{folderCounts.inbox_unread} sin leer</span>}
            </h6>
            {folder === 'trash' && total > 0 && (
              <button className="btn btn-sm btn-outline-danger" disabled={bulkBusy} onClick={emptyTrash}>Vaciar papelera</button>
            )}
            {folder === 'trash' && <span className="text-muted small">Los mensajes de la papelera se eliminan definitivamente a los 30 días.</span>}
            {folder === 'spam' && <span className="text-muted small">Correos descartados por el filtro (spam, newsletters, avisos de sistema, rebotes…). Se borran a los 30 días. Si alguno es de un cliente, márcalo como «No es spam».</span>}
            {folder === 'archived' && <span className="text-muted small">Al responder a un correo, este pasa automáticamente a Archivados.</span>}
          </div>
          {loading ? (
            <p>Cargando...</p>
          ) : isMobile ? (
            <div className="d-flex flex-column gap-2">
              {items.length ? items.map((m) => (
                <div
                  key={m.id}
                  className={`card ${!m.leido && m.direccion === 'in' ? 'border-primary' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Abrir comunicación de ${m.client_name || m.from_address || 'remitente desconocido'}: ${m.asunto || 'sin asunto'}`}
                  onClick={() => handleRowClick(m.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(m.id); } }}
                >
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <span className={`text-truncate ${!m.leido && m.direccion === 'in' ? 'fw-bold' : 'fw-semibold'}`}>
                        {folder === 'sent' ? (m.client_name || m.to_address || '-') : (m.client_name || m.from_address || '-')}
                      </span>
                      <small className="text-muted text-nowrap">{new Date(m.fecha).toLocaleDateString()} {new Date(m.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                    </div>
                    <div className={`text-truncate ${!m.leido && m.direccion === 'in' ? 'fw-bold' : ''}`}>
                      {m.asunto || '(sin asunto)'}
                      {m.has_attachments && <BsPaperclip className="ms-1 text-muted" />}
                      {groupByThread && Number(m._count) > 1 && <span className="badge bg-secondary ms-1">{m._count}</span>}
                    </div>
                    <div className="text-muted small text-truncate">{(m.cuerpo_texto || '').slice(0, 100)}</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      <span className={rowBadgeClass(m)}>{rowStatusLabel(m)}</span>
                      {m.suspicious && <span className="badge bg-danger"><BsShieldX /> Posible phishing</span>}
                      {m.replied_at && <span className="badge bg-light text-dark border"><BsReply /> Respondido</span>}
                      {m.forwarded_at && <span className="badge bg-light text-dark border"><BsForward /> Reenviado</span>}
                      {m.ai_intent && <span className="badge bg-info text-dark">{intentLabel(m.ai_intent)}</span>}
                      {folder === 'spam' && m.discard_reason && <span className="badge bg-light text-muted border">{discardReasonLabel(m.discard_reason)}</span>}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center text-muted py-3">Sin comunicaciones.</div>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>
                      {!groupByThread && (
                        <input type="checkbox" checked={selectAll} onChange={(e) => {
                          setSelectAll(e.target.checked);
                          if (e.target.checked) setSelectedIds(items.map((m) => m.id));
                          else setSelectedIds([]);
                        }} />
                      )}
                    </th>
                    <th style={{ width: 22 }}></th>
                    <th>Fecha</th>
                    <th>{folder === 'sent' ? 'Para' : 'De / Cliente'}</th>
                    <th>Cuenta</th>
                    <th>Asunto</th>
                    <th>Vista previa</th>
                    <th>Estado</th>
                    <th>{folder === 'spam' ? 'Motivo' : 'Intent IA'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? items.map((m) => {
                    const unread = !m.leido && m.direccion === 'in';
                    return (
                    <tr
                      key={m.id}
                      style={{ cursor: 'pointer', background: unread ? 'rgba(13,110,253,0.06)' : undefined }}
                      className={`${unread ? 'fw-bold' : ''} ${m.deleted_at || m.estado === 'spam' ? 'text-muted' : ''}`}
                      onClick={() => handleRowClick(m.id)}
                      tabIndex={0}
                      role="button"
                      aria-label={`Abrir comunicación de ${m.client_name || m.from_address || 'remitente desconocido'}: ${m.asunto || 'sin asunto'}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(m.id);
                        }
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        {!groupByThread && (
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar comunicación de ${m.client_name || m.from_address || 'remitente'}`}
                            checked={selectedIds.includes(m.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSelectedIds((prev) =>
                                e.target.checked ? [...prev, m.id] : prev.filter((id) => id !== m.id)
                              );
                            }}
                          />
                        )}
                      </td>
                      <td onClick={(e) => { e.stopPropagation(); if (m.direccion === 'in') markRead([m.id], !m.leido); }} title={m.direccion === 'in' ? (m.leido ? 'Marcar como no leído' : 'Marcar como leído') : ''}>
                        {m.direccion === 'in' ? (unread ? <BsEnvelope className="text-primary" /> : <BsEnvelopeOpen className="text-muted" />) : <BsSend className="text-muted" />}
                      </td>
                      <td className="text-nowrap">{new Date(m.fecha).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="text-truncate" style={{ maxWidth: 200 }} title={folder === 'sent' ? (m.to_address || '') : (m.from_address || '')}>
                        {folder === 'sent' ? (m.client_name || m.to_address || '-') : (m.client_name || m.from_address || '-')}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 140 }}>{m.email_account || '-'}</td>
                      <td className="text-truncate" style={{ maxWidth: 280 }} title={m.asunto || ''}>
                        {m.asunto || '(sin asunto)'}
                        {m.has_attachments && <BsPaperclip className="ms-1 text-muted" title="Con adjuntos" />}
                        {groupByThread && Number(m._count) > 1 && (
                          <span className={`badge ms-1 ${Number(m._unread) > 0 ? 'bg-primary' : 'bg-secondary'}`} title={Number(m._unread) > 0 ? `${m._unread} sin leer en la conversación` : ''}>{m._count}</span>
                        )}
                      </td>
                      <td className="text-truncate fw-normal" style={{ maxWidth: 300 }} title={m.cuerpo_texto || ''}>{(m.cuerpo_texto || '').slice(0, 80)}</td>
                      <td className="text-nowrap">
                        <span className={rowBadgeClass(m)}>{rowStatusLabel(m)}</span>
                        {m.suspicious && <span className="badge bg-danger ms-1" title="Remitente desconocido que reclama un pago con adjuntos"><BsShieldX /> Phishing?</span>}
                        {m.replied_at && <BsReply className="ms-1 text-success" title={`Respondido el ${new Date(m.replied_at).toLocaleString()}`} />}
                        {m.forwarded_at && <BsForward className="ms-1 text-secondary" title={`Reenviado el ${new Date(m.forwarded_at).toLocaleString()}`} />}
                      </td>
                      <td className="fw-normal">
                        {folder === 'spam'
                          ? <span className="badge bg-light text-muted border">{discardReasonLabel(m.discard_reason)}</span>
                          : (m.ai_intent ? <span className="badge bg-light text-dark border">{intentLabel(m.ai_intent)}</span> : <span className="text-muted">—</span>)}
                      </td>
                    </tr>
                    );
                  }) : (
                    <tr><td colSpan="9" className="text-center text-muted py-3">Sin comunicaciones en {folderLabel(folder)}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!groupByThread && selectedIds.length > 0 && (
            <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted small">{selectedIds.length} seleccionada(s):</span>
              {bulkButtons()}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ width: 110 }}
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n} / pág</option>
                ))}
              </select>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>
      )}
        </div>
      </div>

      {detail && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }} onClick={closeDetail}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" role="dialog" aria-modal="true" aria-labelledby="detailModalTitle" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate" id="detailModalTitle" title={detail.asunto || ''}>
                  {detail.direccion === 'in' ? <BsEnvelopeOpen className="me-2 text-muted" /> : <BsSend className="me-2 text-muted" />}
                  {detail.asunto || '(sin asunto)'}
                </h5>
                <button className="btn-close" aria-label="Cerrar" onClick={closeDetail}></button>
              </div>
                <div className="modal-body">
                {modalMessage && (
                  <div className={`alert alert-${modalMessage.type} py-2 d-flex justify-content-between align-items-center`}>
                    <span>{modalMessage.text}</span>
                    <button type="button" className="btn-close" aria-label="Cerrar aviso" onClick={() => setModalMessage(null)}></button>
                  </div>
                )}
                <div className="row g-1 small mb-2">
                  <div className="col-md-8"><strong>De:</strong> {detail.remitente || detail.from_address || '-'}</div>
                  <div className="col-md-4 text-md-end text-muted">{new Date(detail.fecha).toLocaleString()}</div>
                  <div className="col-12"><strong>Para:</strong> {detail.to_address || detail.destinatario || '-'}</div>
                  {detail.cc_address && <div className="col-12"><strong>CC:</strong> {detail.cc_address}</div>}
                  {detail.bcc_address && <div className="col-12"><strong>CCO:</strong> {detail.bcc_address}</div>}
                  <div className="col-12 d-flex flex-wrap align-items-center gap-2 mt-1">
                    {detail.client_name && (
                      <span className="badge bg-light text-dark border">Cliente: {detail.client_name}</span>
                    )}
                    <span className={rowBadgeClass(detail)}>{rowStatusLabel(detail)}</span>
                    {detail.email_account && <span className="badge bg-light text-muted border">Cuenta: {detail.email_account}</span>}
                    {detail.replied_at && <span className="badge bg-success bg-opacity-75"><BsReply /> Respondido el {new Date(detail.replied_at).toLocaleString()}</span>}
                    {detail.forwarded_at && <span className="badge bg-secondary"><BsForward /> Reenviado el {new Date(detail.forwarded_at).toLocaleString()}</span>}
                    {detail.deleted_at && <span className="badge bg-danger"><BsTrash /> En la papelera</span>}
                  </div>
                </div>
                {detail.bounce && (
                  <div className="alert alert-danger py-2 small">
                    <BsExclamationTriangle className="me-1" /><strong>Este correo NO se entregó.</strong> El servidor del destinatario lo rechazó
                    {detail.bounce.at ? ` el ${new Date(detail.bounce.at).toLocaleString()}` : ''}.
                    {detail.bounce.reason && <div className="text-muted mt-1" style={{ whiteSpace: 'pre-wrap' }}>{String(detail.bounce.reason).slice(0, 400)}</div>}
                  </div>
                )}
                {detail.security_warning && (
                  <div className="alert alert-danger py-2 small">
                    <BsShieldX className="me-1" /><strong>Posible suplantación (phishing):</strong> remitente desconocido que reclama un pago con adjuntos.
                    Por seguridad sus adjuntos <strong>no se han guardado</strong> y la IA no lo ha procesado.
                    No abras enlaces ni adjuntos desde el correo; si dudas, confirma con el proveedor por un teléfono o correo que ya conozcas.
                  </div>
                )}
                {detail.estado === 'spam' && !detail.deleted_at && (
                  <div className="alert alert-warning py-2 small d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <span><BsShieldX className="me-1" />Descartado por el filtro: <strong>{discardReasonLabel(detail.discard_reason)}</strong>. No se ha procesado con IA ni indexado.</span>
                    <button className="btn btn-sm btn-success" disabled={bulkBusy} onClick={() => bulkAction('not_spam', [detail.id])}>No es spam: recuperar a Recibidos</button>
                  </div>
                )}
                {Array.isArray(detail.attachments) && detail.attachments.filter((a) => !a.inline).length > 0 && (
                  <div className="mb-3">
                    <div className="small fw-bold mb-1"><BsPaperclip /> Adjuntos ({detail.attachments.filter((a) => !a.inline).length})</div>
                    <div className="d-flex flex-wrap gap-2">
                      {detail.attachments.filter((a) => !a.inline).map((att) => (
                        <button
                          key={att.index}
                          type="button"
                          className={`btn btn-sm ${att.downloadable ? 'btn-outline-primary' : (att.blocked ? 'btn-outline-danger' : 'btn-outline-secondary')} d-inline-flex align-items-center gap-1`}
                          disabled={!att.downloadable || downloadingAtt === `${detail.id}-${att.index}`}
                          title={att.downloadable ? 'Descargar' : (att.blocked ? 'No se ha guardado por seguridad (posible phishing)' : 'Este adjunto llegó antes de que se guardaran los ficheros: solo hay metadatos')}
                          onClick={() => downloadAttachment(detail.id, att)}
                        >
                          <BsPaperclip />
                          <span className="text-truncate" style={{ maxWidth: 220 }}>{att.filename}</span>
                          {att.size ? <span className="text-muted">({formatFileSize(att.size)})</span> : null}
                          {downloadingAtt === `${detail.id}-${att.index}` && <span className="spinner-border spinner-border-sm" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {isWhatsapp && (
                  <div className="alert alert-info py-2">
                    Mensaje de WhatsApp: sin asunto ni firma. Responde desde la pestaña WhatsApp para mantener el formato de chat.
                  </div>
                )}
                <div className="mb-3">
                  <div className="fw-bold small mb-1">Mensaje</div>
                  <EmailBody text={detail.cuerpo_html || detail.cuerpo_texto} />
                </div>

                {alreadyExecuted && (
                  <div className="alert alert-success py-2 small d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <span>
                      <BsCheck2All className="me-1" /><strong>Aprobado y ejecutado</strong>{detail.ai_executed_at ? ` el ${new Date(detail.ai_executed_at).toLocaleString()}` : ''}.
                      {detail.ai_execution_notes && <span className="text-muted"> {detail.ai_execution_notes}</span>}
                    </span>
                    {executedActions.length > 0 && (
                      <button className="btn btn-sm btn-outline-success" onClick={() => setShowExecutedActions(true)}>Ver acciones realizadas</button>
                    )}
                  </div>
                )}

                {/* Tarjeta de la IA: solo para correo entrante real que la IA haya visto (o pueda ver) */}
                {detail.direccion === 'in' && !isManualNote && detail.estado !== 'spam' && !detail.deleted_at && !alreadyExecuted && (
                  <div className={`card mb-3 ${hasReplyOrActions ? 'border-primary' : 'border-light'}`}>
                    <div className={`card-header py-2 d-flex flex-wrap align-items-center gap-2 ${hasReplyOrActions ? 'bg-primary bg-opacity-10' : 'bg-light'}`}>
                      <BsRobot className="text-primary" /><strong>Propuesta de la IA</strong>
                      {detail.ai_intent && <span className="badge bg-info text-dark">{intentLabel(detail.ai_intent)}</span>}
                      {detail.ai_confidence != null && (
                        <span className={`badge ${detail.ai_confidence >= 0.8 ? 'bg-success' : detail.ai_confidence >= 0.5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                          Confianza: {(detail.ai_confidence * 100).toFixed(0)}%
                        </span>
                      )}
                      {detail.comercial_review && detail.comercial_review.autocorregido && <span className="badge bg-light text-dark border">Revisada y corregida por el revisor</span>}
                    </div>
                    <div className="card-body">
                      {!aiHasRun && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 small">
                          <span className="text-muted">Este correo todavía no ha pasado por la IA.</span>
                          <button className="btn btn-sm btn-outline-primary" disabled={reprocessing} onClick={reprocessWithAi}>
                            {reprocessing ? 'Procesando…' : <><BsRobot className="me-1" />Procesar con IA</>}
                          </button>
                        </div>
                      )}
                      {aiNoReplyNeeded && (
                        <div className="small text-muted"><BsCheck2All className="me-1 text-success" />La IA ha leído el correo y no ve necesaria una respuesta ni ninguna acción. Si quieres contestar, usa «Responder» más abajo.</div>
                      )}
                      {aiDerivedToComercial && (
                        <div className="small text-muted"><BsBriefcase className="me-1" />Oportunidad comercial derivada al agente comercial: él redactará la respuesta cuando se ejecute (o vuelve a procesar con IA para que la redacte el agente de email).</div>
                      )}
                      {aiHasRun && !aiNoReplyNeeded && !aiDerivedToComercial && !hasReplyOrActions && (
                        <div className="small text-muted">La IA lo ha clasificado pero no ha propuesto respuesta ni acciones. Puedes contestar a mano con «Responder» o volver a procesarlo.</div>
                      )}
                      {parsedActions.length > 0 && (
                        <div className="mb-2">
                          <div className="small fw-bold mb-1">Acciones que se ejecutarán al aprobar:</div>
                          <ul className="mb-0 small">
                            {parsedActions.map((a, idx) => (
                              <li key={idx}>
                                <strong>{ACTION_TYPE_LABELS[a.type] || a.type}</strong>{a.title ? `: ${a.title}` : (a.summary ? `: ${a.summary}` : '')}
                                {a.importance && <span className="badge bg-secondary ms-1">{a.importance}</span>}
                                {a.type === 'create_event' && a.date && (
                                  <span className="text-muted ms-1">({a.date}{a.time ? ` ${a.time}` : ''}{a.durationMin ? `, ${a.durationMin} min` : ''})</span>
                                )}
                                {a.type === 'send_invoices' && (
                                  <span className="text-muted ms-1">({a.year ? `Año: ${a.year}` : ''}{a.last ? ` Últimas: ${a.last}` : ''}{a.refs && a.refs.length ? ` Refs: ${a.refs.join(', ')}` : ''})</span>
                                )}
                                {a.type === 'send_budget' && (
                                  <span className="text-muted ms-1">(Adjuntar presupuesto {a.numero || a.budgetId}{a.total ? ` · ${Number(a.total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}` : ''})</span>
                                )}
                                {a.type === 'create_support_ticket' && (
                                  <span className="text-muted ms-1">(Ticket: {a.subject || a.title || ''}{a.priority ? ` · prioridad ${a.priority}` : ''})</span>
                                )}
                                {a.type === 'create_dev_request' && (
                                  <span className="text-muted ms-1">({a.project ? `${a.project} · ` : ''}prioridad {a.priority || 'media'})</span>
                                )}
                                {a.type === 'create_dev_request' && a.description && <div className="small text-muted">{a.description}</div>}
                                {a.comments && <div className="small text-muted">{a.comments}</div>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {detail.comercial_review?.avisos?.length > 0 && (
                        <div className="alert alert-warning py-2 small mt-2 mb-2">
                          <strong>Avisos del revisor del agente:</strong>
                          <ul className="mb-0 ps-3">
                            {detail.comercial_review.avisos.map((aviso, idx) => <li key={idx}>{aviso}</li>)}
                          </ul>
                        </div>
                      )}
                      {detail.ai_suggested_body && (
                        <div className="p-2 border rounded bg-light">
                          <div className="small text-muted mb-1 d-flex justify-content-between align-items-center">
                            <span>Respuesta propuesta{approveEdit ? ' (editada por ti)' : ''}: se enviará a <strong>{detail.from_address || detail.remitente}</strong> al aprobar</span>
                            {approveEdit ? (
                              <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setApproveEdit(null)}>Deshacer edición</button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-primary py-0"
                                onClick={() => setApproveEdit({
                                  subject: detail.ai_suggested_subject || detail.asunto || '',
                                  body: detail.ai_suggested_body || ''
                                })}
                              >Editar antes de aprobar</button>
                            )}
                          </div>
                          {approveEdit ? (
                            <>
                              <input
                                className="form-control form-control-sm mb-2"
                                value={approveEdit.subject}
                                onChange={(e) => setApproveEdit((p) => ({ ...p, subject: e.target.value }))}
                                placeholder="Asunto"
                              />
                              <textarea
                                className="form-control form-control-sm"
                                rows={Math.min(14, Math.max(6, String(approveEdit.body || '').split('\n').length + 1))}
                                value={approveEdit.body}
                                onChange={(e) => setApproveEdit((p) => ({ ...p, body: e.target.value }))}
                              />
                            </>
                          ) : (
                            <>
                              {detail.ai_suggested_subject && (
                                <div className="mb-1 small"><strong>Asunto:</strong> {detail.ai_suggested_subject}</div>
                              )}
                              <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{detail.ai_suggested_body}</div>
                            </>
                          )}
                          {replyEditedInEditor && !approveEdit && (
                            <div className="alert alert-info py-1 px-2 small mt-2 mb-0">
                              Has modificado el texto en el editor de abajo: al aprobar se enviará <strong>tu versión</strong>, no esta propuesta.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isWhatsapp && !detail.deleted_at && (
                  <div className="mt-3">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <div className="btn-group btn-group-sm" role="group" aria-label="Acciones de respuesta">
                        {detail.direccion === 'in' && detail.estado !== 'spam' && (
                          <button type="button" className={`btn ${replyMode === 'reply' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => startReply('reply')}>
                            <BsReply className="me-1" />Responder
                          </button>
                        )}
                        {detail.direccion === 'in' && detail.estado !== 'spam' && (
                          <button type="button" className={`btn ${replyMode === 'replyAll' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => startReply('replyAll')}>
                            <BsReplyAll className="me-1" />Responder a todos
                          </button>
                        )}
                        <button type="button" className={`btn ${replyMode === 'forward' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => startReply('forward')}>
                          <BsForward className="me-1" />Reenviar
                        </button>
                      </div>
                      {replyMode && (
                        <button type="button" className="btn btn-sm btn-link text-muted" onClick={() => setReplyMode(null)}>Ocultar editor</button>
                      )}
                    </div>
                    {replyMode && (
                    <div className="border rounded p-2 bg-light bg-opacity-50">
                    <div className="row g-2 mb-2">
                      <div className="col-md-8">
                        <label className="form-label small mb-1">Para</label>
                        <EmailRecipientInput value={replyTo} onChange={setReplyTo} placeholder="correo@dominio.com" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small mb-1">Cuenta</label>
                        <select
                          className="form-select form-select-sm"
                          value={replyAccountId}
                          onChange={(e) => {
                            const newAccId = e.target.value;
                            const newSig = getSignature(newAccId);
                            setReplyDraft((prev) => replaceSignature(prev, replyLastSignature, newSig));
                            setReplyLastSignature(newSig);
                            setReplyAccountId(newAccId);
                          }}
                        >
                          {emailAccounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>{acc.from_name ? `${acc.from_name} <${acc.from_email}>` : acc.from_email}</option>
                          ))}
                        </select>
                      </div>
                      {showReplyCc ? (
                        <>
                          <div className="col-md-6">
                            <label className="form-label small mb-1">CC</label>
                            <EmailRecipientInput value={replyCc} onChange={setReplyCc} placeholder="opcional" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small mb-1">CCO</label>
                            <EmailRecipientInput value={replyBcc} onChange={setReplyBcc} placeholder="opcional" />
                          </div>
                        </>
                      ) : (
                        <div className="col-12">
                          <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setShowReplyCc(true)}>Añadir CC / CCO</button>
                        </div>
                      )}
                      <div className="col-md-8">
                        <label className="form-label small mb-1">Asunto</label>
                        <input className="form-control form-control-sm" value={replySubject} onChange={(e) => setReplySubject(e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small mb-1">Insertar plantilla</label>
                        <select
                          className="form-select form-select-sm"
                          value={replyTemplateId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setReplyTemplateId(nextId);
                            if (!nextId) return;
                            const tpl = savedTemplates.find((t) => String(t.id) === String(nextId));
                            if (!tpl) return;
                            const sig = getSignature(replyAccountId);
                            setReplyDraft(`${tpl.body_html || ''}${sig || ''}`);
                            setReplyLastSignature(sig);
                          }}
                        >
                          <option value="">(Sin plantilla)</option>
                          {savedTemplates.map((tpl) => (
                            <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <ReactQuill
                      theme="snow"
                      value={replyDraft}
                      onChange={(val) => setReplyDraft(val)}
                      modules={quillModules}
                      style={{ minHeight: 260, background: '#fff' }}
                    />
                    <div className="small text-muted mt-1">
                      La firma del buzón está incluida en el cuerpo.
                      {replyMode === 'forward'
                        ? ' El mensaje original (cabeceras y contenido) se añadirá debajo de tu texto.'
                        : ''}
                    </div>
                    {replyMode !== 'forward' && (
                      <div className="form-check mt-1">
                        <input className="form-check-input" type="checkbox" id="includeQuote" checked={includeQuote} onChange={(e) => setIncludeQuote(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="includeQuote">Citar el mensaje original debajo de la respuesta</label>
                      </div>
                    )}
                    {replyMode === 'forward' && (detail.attachments || []).some((a) => a.downloadable && !a.inline) && (
                      <div className="mt-2">
                        <div className="small fw-bold">Adjuntos originales a incluir</div>
                        {(detail.attachments || []).filter((a) => a.downloadable && !a.inline).map((att) => (
                          <div className="form-check" key={att.index}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`fwd-att-${att.index}`}
                              checked={forwardAttachmentIdx.includes(att.index)}
                              onChange={(e) => setForwardAttachmentIdx((prev) => (e.target.checked ? [...prev, att.index] : prev.filter((i) => i !== att.index)))}
                            />
                            <label className="form-check-label small" htmlFor={`fwd-att-${att.index}`}>
                              {att.filename}{att.size ? ` (${formatFileSize(att.size)})` : ''}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-2">
                      <label className="form-label small mb-1">Adjuntar ficheros</label>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <input
                          type="file"
                          multiple
                          className="form-control form-control-sm"
                          style={{ maxWidth: 260 }}
                          ref={replyFileInputRef}
                          onChange={(e) => uploadReplyFiles(e.target.files)}
                        />
                        {replyUploading && <span className="text-muted small">Subiendo...</span>}
                      </div>
                      {replyAttachments.length > 0 && (
                        <ul className="list-group list-group-flush mt-1">
                          {replyAttachments.map((att) => (
                            <li key={att.storageName || att.id} className="list-group-item d-flex justify-content-between align-items-center py-1">
                              <span className="me-2 small">
                                {att.originalName || att.filename || att.storageName || 'adjunto'}
                                {att.size ? ` (${formatFileSize(att.size)})` : ''}
                              </span>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => removeReplyAttachment(att)}>Quitar</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={sendingReply || replyUploading}
                        onClick={sendFromEditor}
                      >
                        {sendingReply ? 'Enviando…' : (replyMode === 'forward' ? <><BsForward className="me-1" />Reenviar</> : <><BsSend className="me-1" />Enviar respuesta</>)}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(stripHtml(replyDraft) || '');
                        }}
                      >
                        Copiar texto
                      </button>
                    </div>
                    </div>
                    )}
                  </div>
                )}

                {threadMessages.length > 1 && (
                  <div className="mt-3">
                    <strong>Hilo de conversaci&oacute;n ({threadMessages.length}):</strong>
                    <div className="accordion accordion-flush mt-2">
                      {threadMessages.filter((tm) => tm.id !== detail.id).map((tm) => {
                        const isOpen = openThreadId === tm.id;
                        return (
                          <div className="accordion-item" key={tm.id}>
                            <h2 className="accordion-header">
                              <button
                                className={`accordion-button py-2 small ${isOpen ? '' : 'collapsed'}`}
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => setOpenThreadId(isOpen ? null : tm.id)}
                              >
                                <span className="me-2" title={tm.direccion === 'in' ? 'Recibido' : 'Enviado por nosotros'}>{tm.direccion === 'in' ? '\u2190' : '\u2192'}</span>
                                <span className={`me-2 ${tm.direccion === 'out' ? 'text-success fw-bold' : 'fw-bold'}`}>{tm.direccion === 'out' ? `Tú (${tm.from_address || ''})` : (tm.from_address || 'sin remitente')}</span>
                                <span className="text-muted me-2">{new Date(tm.fecha).toLocaleDateString()} {new Date(tm.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="text-truncate">{tm.asunto || '(sin asunto)'}</span>
                              </button>
                            </h2>
                            {isOpen && (
                              <div className="accordion-collapse">
                                <div className="accordion-body small">
                                  <div className="text-muted mb-1">Para: {tm.to_address || '-'}{tm.cc_address ? ` · CC: ${tm.cc_address}` : ''}</div>
                                  <EmailBody text={tm.cuerpo_html || tm.cuerpo_texto} />
                                  {Array.isArray(tm.attachments) && tm.attachments.filter((a) => !a.inline).length > 0 && (
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                      {tm.attachments.filter((a) => !a.inline).map((att) => (
                                        <button key={att.index} type="button" className="btn btn-sm btn-outline-secondary py-0" disabled={!att.downloadable} onClick={() => downloadAttachment(tm.id, att)}>
                                          <BsPaperclip /> {att.filename}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  <div className="mt-2">
                                    <button type="button" className="btn btn-sm btn-link p-0" onClick={() => { closeDetail(); handleRowClick(tm.id); }}>Abrir este mensaje</button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer flex-wrap gap-1">
                <div className="d-flex flex-wrap gap-1 me-auto">
                  {detail.direccion === 'in' && !detail.deleted_at && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => markRead([detail.id], !detail.leido)} title={detail.leido ? 'Marcar como no leído' : 'Marcar como leído'}>
                      {detail.leido ? <><BsEnvelope className="me-1" />No leído</> : <><BsEnvelopeOpen className="me-1" />Leído</>}
                    </button>
                  )}
                  {detail.direccion === 'in' && !detail.deleted_at && !['archivado', 'spam'].includes(detail.estado) && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => bulkAction('archive', [detail.id])}><BsArchive className="me-1" />Archivar</button>
                  )}
                  {!detail.deleted_at && detail.estado === 'archivado' && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => bulkAction('unarchive', [detail.id])}><BsInbox className="me-1" />Devolver a Recibidos</button>
                  )}
                  {detail.direccion === 'in' && !detail.deleted_at && detail.estado !== 'spam' && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={bulkBusy} onClick={() => bulkAction('spam', [detail.id])} title="Mover a Descartados"><BsShieldX className="me-1" />Descartar</button>
                  )}
                  {!detail.deleted_at ? (
                    <button className="btn btn-sm btn-outline-danger" disabled={bulkBusy} onClick={() => bulkAction('trash', [detail.id])}><BsTrash className="me-1" />Papelera</button>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-outline-success" disabled={bulkBusy} onClick={() => bulkAction('restore', [detail.id])}><BsArrowCounterclockwise className="me-1" />Restaurar</button>
                      <button className="btn btn-sm btn-danger" disabled={bulkBusy} onClick={() => bulkAction('delete', [detail.id], { confirmText: '¿Eliminar definitivamente este mensaje? No se puede deshacer.' })}>Eliminar definitivamente</button>
                    </>
                  )}
                </div>
                {detail.direccion === 'in' && !isManualNote && !alreadyExecuted && !detail.deleted_at && detail.estado !== 'spam' && aiHasRun && (
                  <button className="btn btn-outline-primary" disabled={reprocessing || executingActions} onClick={reprocessWithAi} title="Descarta la propuesta actual y vuelve a pasar el correo por la IA">
                    {reprocessing ? 'Reprocesando…' : 'Volver a procesar IA'}
                  </button>
                )}
                <button className="btn btn-outline-secondary" onClick={closeDetail}>Cerrar</button>
                {canShowApprove && (
                  <button
                    className="btn btn-primary"
                    disabled={executingActions || reprocessing}
                    onClick={() => {
                      const parts = [];
                      if (detail.ai_suggested_body) parts.push(`enviar la respuesta a ${detail.from_address || detail.remitente}${replyEditedInEditor || approveEdit ? ' (tu versión editada)' : ''}`);
                      if (parsedActions.length) parts.push(`ejecutar ${parsedActions.length} acción(es): ${parsedActions.map((a) => ACTION_TYPE_LABELS[a.type] || a.type).join(', ')}`);
                      const q = `${detail.replied_at ? 'Ya has respondido manualmente a este correo. ' : ''}¿Aprobar? Se va a ${parts.join(' y ')}.`;
                      if (!window.confirm(q)) return;
                      executeAiActions();
                    }}
                  >
                    {executingActions ? 'Ejecutando...' : approveLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {composeOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {composeEditingId
                    ? (composeEditingStatus === 'pending' ? 'Editar email programado' : 'Editar borrador')
                    : 'Redactar email'}
                </h5>
                <button className="btn-close" onClick={() => handleCloseCompose()}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <label className="form-label small">Cuenta</label>
                    <select
                      className="form-select"
                      value={compose.accountId}
                      onChange={(e) => {
                        const newAccId = e.target.value;
                        const newSig = getSignature(newAccId);
                        setCompose((p) => ({
                          ...p,
                          accountId: newAccId,
                          body: replaceSignature(p.body, p.lastSignature, newSig),
                          lastSignature: newSig
                        }));
                      }}
                    >
                      {emailAccounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>{acc.from_name ? `${acc.from_name} <${acc.from_email}>` : acc.from_email}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Para</label>
                      <EmailRecipientInput value={compose.to} onChange={(val) => setCompose((p) => ({ ...p, to: val }))} placeholder="correo@dominio.com" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">CC</label>
                      <EmailRecipientInput value={compose.cc} onChange={(val) => setCompose((p) => ({ ...p, cc: val }))} placeholder="opcional" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">BCC</label>
                    <EmailRecipientInput value={compose.bcc} onChange={(val) => setCompose((p) => ({ ...p, bcc: val }))} placeholder="opcional" />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">Asunto</label>
                    <input className="form-control" value={compose.subject} onChange={(e) => setCompose((p) => ({ ...p, subject: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Plantilla guardada</label>
                    <select
                      className="form-select"
                      value={selectedTemplateId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        setSelectedTemplateId(nextId);
                        if (!nextId) return;
                        const tpl = savedTemplates.find((t) => String(t.id) === String(nextId));
                        if (!tpl) return;
                        const sig = getSignature(compose.accountId);
                        setCompose((p) => ({
                          ...p,
                          body: `${tpl.body_html || ''}${sig || ''}`,
                          lastSignature: sig
                        }));
                      }}
                    >
                      <option value="">(Sin plantilla)</option>
                      {savedTemplates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label small">Cuerpo</label>
                    <ReactQuill
                      theme="snow"
                      value={compose.body}
                      onChange={(val) => setCompose((p) => ({ ...p, body: val }))}
                      modules={quillModules}
                      style={{ minHeight: 320 }}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">Adjuntos</label>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <input
                        type="file"
                        multiple
                        className="form-control"
                        style={{ maxWidth: 260 }}
                        ref={composeFileInputRef}
                        onChange={(e) => uploadComposeFiles(e.target.files)}
                      />
                      {composeUploading && <span className="text-muted small">Subiendo...</span>}
                      <span className="text-muted small">Se eliminan del servidor tras enviarlos.</span>
                    </div>
                    {composeAttachments.length > 0 && (
                      <ul className="list-group list-group-flush mt-2">
                        {composeAttachments.map((att) => (
                          <li
                            key={att.storageName || att.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span className="me-2">
                              {att.originalName || att.filename || att.storageName || 'adjunto'}
                              {att.size ? ` (${formatFileSize(att.size)})` : ''}
                            </span>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeComposeAttachment(att)}
                            >
                              Quitar
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Programar envío</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={compose.sendAt}
                      onChange={(e) => setCompose((p) => ({ ...p, sendAt: e.target.value }))}
                    />
                    <div className="small text-muted">Deja vacío para enviar ahora o usa el botón Enviar ahora.</div>
                    {composeEditingId && (
                      <div className="small text-muted">Puedes enviarlo ya, guardarlo como borrador o guardar la programación editada.</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => handleCloseCompose()}>Cerrar</button>
                <button
                  className="btn btn-outline-primary"
                  disabled={composeLoading || composeUploading}
                  onClick={async () => {
                    if (composeUploading) return alert('Espera a que los adjuntos terminen de subir');
                    if (!compose.accountId) return alert('Selecciona cuenta');
                    if (!compose.to) return alert('Destinatario requerido');
                    setComposeLoading(true);
                    try {
                      const payload = buildComposePayload();
                      // Al editar un borrador o un programado se guarda y se envía por la misma
                      // vía que el cron (queda marcado como enviado, no se duplica)
                      const endpoint = composeEditingId ? `/api/email/scheduled/${composeEditingId}/send-now` : '/api/email/send';
                      const res = await apiFetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify(payload)
                      });
                      const data = await parseJsonSafe(res);
                      if (res.status === 401) return handleUnauthorized();
                      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
                      alert('Email enviado');
                      await handleCloseCompose(true);
                      fetchData();
                      if (folder === 'scheduled' || folder === 'drafts') fetchScheduled();
                      fetchFolderCounts();
                    } catch (err) {
                      alert(err.message);
                    } finally {
                      setComposeLoading(false);
                    }
                  }}
                >
                  {composeLoading ? 'Enviando...' : 'Enviar ahora'}
                </button>
                <button
                  className="btn btn-outline-success"
                  disabled={composeLoading || composeUploading}
                  onClick={async () => {
                    if (composeUploading) return alert('Espera a que los adjuntos terminen de subir');
                    if (!compose.accountId) return alert('Selecciona cuenta');
                    setComposeLoading(true);
                    try {
                      await saveComposeScheduled('draft');
                      alert(composeEditingId ? 'Borrador actualizado' : 'Borrador guardado');
                      await handleCloseCompose(true);
                      if (folder === 'drafts') fetchScheduled('draft'); else changeFolder('drafts');
                    } catch (err) {
                      alert(err.message);
                    } finally {
                      setComposeLoading(false);
                    }
                  }}
                >
                  {composeLoading ? 'Guardando...' : 'Guardar borrador'}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  disabled={composeLoading || composeUploading || templateSaving}
                  onClick={async () => {
                    if (composeUploading) return alert('Espera a que los adjuntos terminen de subir');
                    if (!compose.body) return alert('Escribe un cuerpo para guardar');
                    const defaultName = compose.subject?.trim() || 'Plantilla sin nombre';
                    const name = window.prompt('Nombre de la plantilla', defaultName);
                    if (!name) return;
                    setTemplateSaving(true);
                    try {
                      const res = await apiFetch('/api/email/templates', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ name, body_html: compose.body || '' })
                      });
                      const data = await parseJsonSafe(res);
                      if (res.status === 401) return handleUnauthorized();
                      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la plantilla');
                      alert('Plantilla guardada');
                      fetchTemplates();
                    } catch (err) {
                      alert(err.message);
                    } finally {
                      setTemplateSaving(false);
                    }
                  }}
                >
                  {templateSaving ? 'Guardando...' : 'Guardar plantilla'}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={composeLoading || composeUploading}
                  onClick={async () => {
                    if (composeUploading) return alert('Espera a que los adjuntos terminen de subir');
                    if (!compose.accountId) return alert('Selecciona cuenta');
                    if (!compose.to) return alert('Destinatario requerido');
                    if (!compose.sendAt) return alert('Selecciona fecha y hora');
                    setComposeLoading(true);
                      try {
                        await saveComposeScheduled('pending');
                        alert(composeEditingId ? 'Email programado actualizado' : 'Email programado');
                        await handleCloseCompose(true);
                        setScheduledStatus('pending');
                        if (folder === 'scheduled') fetchScheduled('pending'); else changeFolder('scheduled');
                      } catch (err) {
                        alert(err.message);
                      } finally {
                        setComposeLoading(false);
                    }
                  }}
                >
                  {composeLoading ? 'Guardando...' : (composeEditingId ? 'Guardar programación' : 'Programar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {templateModalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar plantilla</h5>
                <button className="btn-close" onClick={() => setTemplateModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small">Nombre</label>
                  <input
                    className="form-control"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label small">Cuerpo</label>
                  <ReactQuill
                    theme="snow"
                    value={templateForm.body}
                    onChange={(val) => setTemplateForm((p) => ({ ...p, body: val }))}
                    modules={quillModules}
                    style={{ minHeight: 320 }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setTemplateModalOpen(false)}>Cerrar</button>
                <button
                  className="btn btn-primary"
                  disabled={templateSaving}
                  onClick={async () => {
                    if (!templateForm.id) return alert('Plantilla inválida');
                    if (!templateForm.name || !templateForm.body) return alert('Nombre y cuerpo son obligatorios');
                    setTemplateSaving(true);
                    try {
                      const res = await apiFetch(`/api/email/templates/${templateForm.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ name: templateForm.name, body_html: templateForm.body })
                      });
                      const data = await parseJsonSafe(res);
                      if (res.status === 401) return handleUnauthorized();
                      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
                      setTemplateModalOpen(false);
                      fetchTemplates();
                    } catch (err) {
                      alert(err.message);
                    } finally {
                      setTemplateSaving(false);
                    }
                  }}
                >
                  {templateSaving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfig && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración de lectura automática</h5>
                <button className="btn-close" onClick={() => setShowConfig(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2 fw-bold">Leer comunicaciones cada:</div>
                <div className="d-flex gap-2 mb-3">
                  <input
                    type="number"
                    min="0"
                    max="24"
                    className="form-control"
                    value={pollIntervalValue}
                    onChange={(e) => setPollIntervalValue(Number(e.target.value))}
                  />
                  <select className="form-select" value={pollIntervalUnit} onChange={(e) => setPollIntervalUnit(e.target.value)}>
                    <option value="minutes">minutos</option>
                    <option value="hours">horas</option>
                    <option value="days">días</option>
                  </select>
                </div>
                <div className="text-muted small mb-3">Si el valor es 0, no se programa lectura automática.</div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="autoProcessIA"
                    checked={pollAutoProcess}
                    onChange={(e) => setPollAutoProcess(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="autoProcessIA">
                    Procesar con IA automáticamente tras leer emails
                  </label>
                  <div className="small text-muted">
                    Si está activo, cada lectura automática enviará los candidatos al agente IA.
                  </div>
                </div>

                <div className="mb-2 fw-bold">Usuario por defecto al crear tareas por el agente IA</div>
                <select
                  className="form-select"
                  value={defaultTaskUserId}
                  onChange={(e) => setDefaultTaskUserId(e.target.value)}
                  disabled={loadingUsers}
                >
                  <option value="">(Sin asignar)</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email || `Usuario ${u.id}`}</option>
                  ))}
                </select>
                {loadingUsers && <div className="text-muted small">Cargando usuarios...</div>}

                <hr />
                <div className="mb-2 fw-bold">Entrenamiento IA</div>
                <div className="small text-muted mb-2">
                  Indexa los datos de tu negocio para que la IA tenga contexto completo al responder.
                </div>

                {(syncStatus?.active) && (
                  <div className="alert alert-info py-2 small mb-2">
                    <strong>{syncStatus.type === 'historical-imap' ? 'Importando emails...' : syncStatus.type === 'index-all' ? 'Indexando datos...' : 'Generando perfiles...'}</strong>
                    {syncStatus.progress > 0 && <span> ({syncStatus.progress}%)</span>}
                    <div className="progress mt-1" style={{ height: '4px' }}>
                      <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${Math.max(syncStatus.progress || 0, 5)}%` }}></div>
                    </div>
                  </div>
                )}

                {syncStatus && !syncStatus.active && syncStatus.status === 'completed' && (
                  <div className="alert alert-success py-2 small mb-2">
                    Completado.{' '}
                    {syncStatus.result && typeof syncStatus.result === 'object' && (
                      syncStatus.type === 'historical-imap'
                        ? <span>Importados: {syncStatus.result.totalImported}, Embeddings: {syncStatus.result.totalEmbedded}, Omitidos: {syncStatus.result.totalSkipped}</span>
                        : syncStatus.type === 'generate-profiles'
                          ? <span>Perfiles: {syncStatus.result.generated} generados de {syncStatus.result.total}{syncStatus.result.errors > 0 && <span className="text-danger ms-1">({syncStatus.result.errors} errores)</span>}</span>
                          : <span>Proceso completado correctamente</span>
                    )}
                  </div>
                )}

                {syncStatus && syncStatus.status === 'error' && (
                  <div className="alert alert-danger py-2 small mb-2">Error: {syncStatus.error}</div>
                )}

                {countResult && (
                  <div className="alert alert-secondary py-2 small mb-2">
                    <strong>Emails en servidor:</strong>
                    {countResult.accounts?.map((a, i) => (
                      <div key={i}>
                        <span className="fw-bold">{a.account}:</span>
                        {a.error ? <span className="text-danger"> Error: {a.error}</span> : a.folders?.map((f, j) => (
                          <span key={j}> {f.folder}: {f.messages}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2 mb-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={syncStatus?.active || syncLoading['count-imap']}
                    onClick={() => handleSyncAction('count-imap')}
                  >
                    {syncLoading['count-imap'] ? 'Contando...' : 'Contar emails'}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-warning"
                    disabled={syncStatus?.active}
                    onClick={() => handleSyncAction('historical-imap')}
                  >
                    Importar historico
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    disabled={syncStatus?.active}
                    onClick={() => handleSyncAction('index-all')}
                  >
                    Indexar datos
                  </button>
                  <button
                    className="btn btn-sm btn-outline-info"
                    disabled={syncStatus?.active}
                    onClick={() => handleSyncAction('generate-profiles')}
                  >
                    Generar perfiles
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowConfig(false)}>Cerrar</button>
                <button className="btn btn-primary" disabled={savingConfig} onClick={async () => { await savePollConfig(); setShowConfig(false); }}>
                  {savingConfig ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExecutedActions && detail && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Acciones realizadas</h5>
                <button className="btn-close" onClick={() => setShowExecutedActions(false)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-1"><strong>Ejecutado:</strong> {detail.ai_executed_at ? new Date(detail.ai_executed_at).toLocaleString() : '-'}</p>
                {detail.ai_execution_notes && <p className="mb-2"><strong>Notas:</strong> {detail.ai_execution_notes}</p>}
                <div>
                  <strong>Acciones:</strong>
                  {executedActions.length ? (
                    <ul className="mb-0">
                      {executedActions.map((a, idx) => (
                        <li key={idx}>
                          <strong>{ACTION_TYPE_LABELS[a.type] || a.type}</strong>{a.title ? `: ${a.title}` : (a.summary ? `: ${a.summary}` : '')}
                          {a.importance && <span className="badge bg-secondary ms-1">{a.importance}</span>}
                          {a.type === 'create_event' && a.date && (
                            <span className="text-muted ms-1">({a.date}{a.time ? ` ${a.time}` : ''}{a.durationMin ? `, ${a.durationMin} min` : ''})</span>
                          )}
                          {a.type === 'send_invoices' && (
                            <span className="text-muted ms-1">({a.year ? `Año: ${a.year}` : ''}{a.last ? ` Últimas: ${a.last}` : ''})</span>
                          )}
                          {a.type === 'send_budget' && (
                            <span className="text-muted ms-1">(Presupuesto {a.numero || a.budgetId} adjunto)</span>
                          )}
                          {a.type === 'create_support_ticket' && (
                            <span className="text-muted ms-1">(Ticket: {a.subject || a.title || ''})</span>
                          )}
                          {a.comments && <div className="small text-muted">{a.comments}</div>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted small">No hay acciones registradas.</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setShowExecutedActions(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLogs && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Logs gpt-5-nano (última lectura)</h5>
                <button className="btn-close" onClick={() => setShowLogs(false)}></button>
              </div>
              <div className="modal-body">
                {loadingLogs ? <p>Cargando...</p> : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Remitente</th>
                          <th>Asunto</th>
                          <th>¿Candidato?</th>
                          <th>Respuesta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nanoLogs.length === 0 && <tr><td colSpan="5">Sin registros</td></tr>}
                        {nanoLogs.map((log) => {
                          let parsed;
                          try { parsed = typeof log.raw_response === 'string' ? JSON.parse(log.raw_response) : log.raw_response; } catch (e) { parsed = null; }
                          return (
                            <tr key={log.id}>
                              <td>{new Date(log.created_at).toLocaleString()}</td>
                              <td>{log.from_email || '-'}</td>
                              <td>{log.subject || '-'}</td>
                              <td>{log.candidate ? 'Sí' : 'No'}</td>
                              <td style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                                {parsed ? JSON.stringify(parsed, null, 2) : (log.raw_response || '')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowLogs(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationsPage;
